// src/components/EventViewModal.jsx
// LOOP EVENT VIEW MODAL — RESCHEDULE UX CLEANUP HOTFIX
// LOOP PHASE 2.5D — payment/status guardrails + commission lifecycle
// LOOP EVENT VIEW UX V2 A2.2A — professional hierarchy + progressive disclosure
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { isApprovalFirstEvent } from "../utils/eventBookingFlow";
import EventQuoteCard from './events/EventQuoteCard';
import EventManualReceiptCard from './events/EventManualReceiptCard';
import EventManualRefundCard from './events/EventManualRefundCard';
import EventPriceChangeCard from './events/EventPriceChangeCard';
import { db } from "../firebase";
import AccessibleModal from "./AccessibleModal";
import { bookingScheduledAtToDate } from "../utils/dateUtils";
import { normalizeLoopBusinessSettings, normalizeWorkerPermissions } from "../adminCalendar/workerPermissions";
import { buildBookingLifecyclePatch } from "../utils/bookingLifecycle";
import { reprocessBookingLoyaltyAction, summarizeReprocessResult, reverseLoyaltyRedemptionAction, summarizeReverseLoyaltyRedemptionResult, reprocessBookingMembershipBenefitAction, summarizeMembershipBenefitResult } from "../adminCalendar/bookingActions";

/*
  Props:
    - open, onClose, booking, employees, services, businessId, onAccept, onReject, onCancelBooking, onRemoveFromAgenda, onEdit
    - clientHistory, statusFlowItems, onStatusChange, statusWorkflowSaving
*/

function money(value) {
  const n = Number(value || 0);
  return `$${Number.isFinite(n) ? (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)) : "0"}`;
}

function moneyNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}


function evmMembershipSummary(booking = {}) {
  const applied = booking?.membershipApplied === true || booking?.membershipBenefitApplied === true || Boolean(booking?.customerMembershipId || booking?.membershipId);
  if (!applied) return null;
  const planName = String(booking.membershipPlanName || booking.membershipBenefitPlanName || "Membresía").trim() || "Membresía";
  const type = String(booking.membershipBenefitType || (Number(booking.membershipCreditsUsed || 0) > 0 ? "credit" : "discount")).toLowerCase();
  const credits = Math.max(0, Number(booking.membershipCreditsUsed || 0) || 0);
  const discount = Math.max(0, Number(booking.membershipDiscountPercent || 0) || 0);
  const processed = booking.membershipBenefitProcessed === true || booking.membershipBenefitStatus === "processed" || booking.membershipBenefitStatus === "credit_used";
  const skipped = String(booking.membershipBenefitStatus || "").toLowerCase().includes("skip") || String(booking.membershipBenefitLastSkipReason || "").trim();
  const label = booking.membershipBenefitLabel || (type === "discount" ? `${discount}% descuento · ${planName}` : `${credits || 1} crédito · ${planName}`);
  const state = processed ? "processed" : skipped ? "review" : "pending";
  const stateLabel = processed ? (type === "discount" ? "Descuento aplicado" : "Crédito descontado") : skipped ? "Revisar membresía" : "Pendiente al completar/pagar";
  return { planName, type, credits: credits || (type === "credit" ? 1 : 0), discount, label, state, stateLabel };
}


function normalizeCommissionType(value) {
  const raw = String(value || "none").trim().toLowerCase();
  if (["percentage", "percent", "%", "porcentaje"].includes(raw)) return "percentage";
  if (["fixed", "flat", "amount", "monto"].includes(raw)) return "fixed";
  return "none";
}

function payoutStatusLabel(value) {
  const s = String(value || "pending").toLowerCase();
  if (s === "paid") return "Pagado al trabajador";
  return "Pendiente al trabajador";
}

function paymentStatusLabel(value) {
  const s = String(value || "pending").toLowerCase();
  if (s === "paid") return "Pagado";
  if (s === "covered") return "Cubierto";
  if (s === "partial") return "Parcial";
  if (s === "refunded") return "Reembolsado";
  return "Pendiente";
}

function paymentMethodLabel(value) {
  const s = String(value || "").toLowerCase();
  if (s === "cash") return "Efectivo";
  if (s === "yappy") return "Yappy";
  if (s === "card") return "Tarjeta";
  if (s === "transfer") return "Transferencia";
  if (s === "membership") return "Membresía";
  if (s === "loyalty_reward") return "Fidelidad";
  if (s === "courtesy") return "Cortesía";
  if (s === "other") return "Otro";
  return "Sin método";
}

function inventoryCostFromBooking(booking = {}) {
  const direct = moneyNumber(booking.inventoryCostAmount ?? booking.materialCostAmount ?? booking.inventoryCost ?? 0, 0);
  if (direct > 0) return direct;
  const rows = Array.isArray(booking.inventoryConsumed) ? booking.inventoryConsumed : [];
  return rows.reduce((sum, row) => sum + moneyNumber(row?.totalCost ?? (moneyNumber(row?.quantityUsed, 0) * moneyNumber(row?.unitCost, 0)), 0), 0);
}

function inventoryStatusLabel(booking = {}) {
  const s = String(booking.inventoryDeductionStatus || "").toLowerCase();
  if (s === "deducted") return "Inventario descontado";
  if (s === "error") return "Error descontando inventario";
  if (Array.isArray(booking.inventoryConsumed) && booking.inventoryConsumed.length) return "Consumo registrado";
  return "Sin consumo registrado";
}

function evmIsClosedStatus(value) {
  const s = normalizeStatus(value);
  return ["completed", "cancelled", "rejected", "no_show"].includes(s);
}

function evmStatusNeedsPaymentDecision(nextStatus, paymentStatus, chargedAmount) {
  const status = normalizeStatus(nextStatus);
  const payment = String(paymentStatus || "pending").toLowerCase();
  const charged = moneyNumber(chargedAmount, 0);
  return status === "completed" && !["paid", "covered"].includes(payment) && charged <= 0;
}

function evmCommissionLifecycleStatus({ bookingStatus, paymentStatus, commissionOnlyCompleted = true } = {}) {
  const status = normalizeStatus(bookingStatus);
  const payment = String(paymentStatus || "pending").toLowerCase();
  if (["cancelled", "rejected", "no_show"].includes(status)) return "not_applicable";
  if (commissionOnlyCompleted && status !== "completed") return payment === "paid" ? "held_until_completed" : "estimated";
  if (status === "completed" && payment === "paid") return "payable";
  if (status === "completed" && payment === "partial") return "earned_pending_balance";
  if (status === "completed") return "earned_pending_collection";
  return "estimated";
}

function evmCommissionLifecycleLabel(value) {
  const labels = {
    estimated: "Comisión estimada",
    held_until_completed: "Pago cobrado · falta completar",
    earned_pending_collection: "Ganada · falta cobrar",
    earned_pending_balance: "Ganada · saldo pendiente",
    payable: "Lista para pagar",
    not_applicable: "No aplica",
  };
  return labels[String(value || "estimated")] || labels.estimated;
}


function normalizeRetailRows(value) {
  return (Array.isArray(value) ? value : [])
    .map((row, idx) => ({
      id: row?.id || `retail-${idx}`,
      itemId: String(row?.itemId || row?.inventoryItemId || "").trim(),
      itemName: row?.itemName || row?.name || "Producto",
      quantity: String(row?.quantity ?? row?.qty ?? 1),
      unitPrice: String(row?.unitPrice ?? row?.price ?? ""),
      unitCost: String(row?.unitCost ?? row?.cost ?? ""),
      workerCommissionType: normalizeCommissionType(row?.workerCommissionType || row?.commissionType || "none"),
      workerCommissionValue: String(row?.workerCommissionValue ?? row?.commissionValue ?? ""),
    }))
    .filter((row) => row.itemId || row.itemName);
}

function retailNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function retailRowsTotal(rows = []) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    const qty = retailNumber(row.quantity, 0);
    const price = retailNumber(row.unitPrice, 0);
    return sum + Math.max(0, qty * price);
  }, 0);
}

function retailRowsPayout(rows = []) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    const qty = retailNumber(row.quantity, 0);
    const price = retailNumber(row.unitPrice, 0);
    const lineTotal = Math.max(0, qty * price);
    const value = retailNumber(row.workerCommissionValue, 0);
    const type = normalizeCommissionType(row.workerCommissionType);
    if (type === "percentage") return sum + Math.max(0, lineTotal * (value / 100));
    if (type === "fixed") return sum + Math.max(0, value);
    return sum;
  }, 0);
}

function getServiceInventoryUsageRows(service = {}, inventoryItems = []) {
  const raw = Array.isArray(service?.inventoryUsage) ? service.inventoryUsage : Array.isArray(service?.inventoryConsumption) ? service.inventoryConsumption : [];
  return raw
    .map((row, idx) => {
      const itemId = String(row?.itemId || row?.inventoryItemId || "").trim();
      const item = inventoryItems.find((it) => String(it.id) === itemId) || {};
      const quantityUsed = retailNumber(row?.quantityUsed ?? row?.quantity ?? row?.qty, 0);
      const unitCost = retailNumber(item.unitCost ?? item.cost ?? row?.unitCost, 0);
      if (!itemId || quantityUsed <= 0) return null;
      return { id: row?.id || `usage-${itemId}-${idx}`, itemId, itemName: item.name || item.title || row?.itemName || itemId, unit: item.unit || row?.unit || "un.", quantityUsed, unitCost, totalCost: quantityUsed * unitCost, estimated: true };
    })
    .filter(Boolean);
}

function getEventDetails(booking = {}) {
  const details = booking.eventDetails || {};
  const options = booking.eventOptions || {};

  const selectedCharacters =
    Array.isArray(details.selectedCharacters) ? details.selectedCharacters :
    Array.isArray(options.selectedCharacters) ? options.selectedCharacters :
    [];

  const selectedCharacterIds =
    Array.isArray(details.selectedCharacterIds) ? details.selectedCharacterIds :
    Array.isArray(options.selectedCharacterIds) ? options.selectedCharacterIds :
    [];

  return {
    isEvent: Boolean(
      booking.eventDetails ||
      booking.eventOptions ||
      booking.totalPrice != null ||
      booking.assignmentStatus === "not_required" ||
      booking.employeeNotRequired === true
    ),
    visitTypeLabel: details.visitTypeLabel || options.visitTypeLabel || details.visitType || options.visitType || "",
    visitHours: details.visitHours || options.visitHours || null,
    selectedCharacters,
    selectedCharacterIds,
    includedCharacters: details.includedCharacters || options.includedCharacters || [],
    selectedExtras: details.selectedExtras || options.selectedExtras || [],
    durationPrice: details.durationPrice ?? null,
    charactersPrice: details.charactersPrice ?? null,
    totalPrice: booking.totalPrice ?? details.totalPrice ?? options.totalPrice ?? null,
  };
}

function getBookingLocationInfo(booking = {}) {
  const loc = booking.customerLocation || booking.location || {};
  const matched = booking.serviceAreaMatched || booking.matchedServiceArea || {};
  const exact = String(booking.customerExactAddress || booking.customerAddressDetails || booking.exactAddress || booking.addressDetails || booking.deliveryAddress || booking.customerAddress || "").trim();
  const zone = String(loc.label || matched.label || booking.customerLocationLabel || booking.serviceAreaLabel || booking.coverageArea || "").trim();
  const modeRaw = String(booking.visitMode || booking.visitTypeLocation || booking.serviceMode || "").toLowerCase();
  const isDelivery = modeRaw === "delivery" || modeRaw === "domicilio" || Boolean(booking.customerLocation || booking.serviceAreaMatched || exact);
  const fee = Number(booking.deliveryFee ?? booking.locationFee ?? matched.extraFee ?? matched.fee ?? 0);
  return { isDelivery, modeLabel: isDelivery ? "A domicilio" : "En local", zone, exact, fee: Number.isFinite(fee) ? fee : 0 };
}

function normalizeStatus(value) {
  const s = String(value || "").trim().toLowerCase();
  if (["approved", "accepted", "confirmada", "confirmed", "aprobada"].includes(s)) return "confirmed";
  if (["pendiente", "pending", "to_confirm", "por_confirmar"].includes(s)) return "pending";
  if (["started", "in-progress", "in_progress", "en_progreso"].includes(s)) return "in_progress";
  if (["done", "complete", "completed", "completada"].includes(s)) return "completed";
  if (["no-show", "noshow", "no_show", "no asistió", "no_asistio"].includes(s)) return "no_show";
  if (["cancelled", "canceled", "cancelada"].includes(s)) return "cancelled";
  if (["rejected", "declined", "rechazada"].includes(s)) return "rejected";
  return s || "confirmed";
}

function LocationSummaryCard({ booking }) {
  const info = getBookingLocationInfo(booking);
  return (
    <section className={`evm-card evm-location ${info.isDelivery ? "is-delivery" : "is-local"}`}>
      <div className="evm-location-top">
        <div>
          <span>Atención</span>
          <strong>{info.modeLabel}</strong>
        </div>
        <em>{info.isDelivery ? "Domicilio" : "Local"}</em>
      </div>
      {info.zone || info.fee > 0 ? (
        <div className="evm-soft-box">
          <span>Zona asignada</span>
          <strong>{info.zone || "Zona no especificada"}{info.fee > 0 ? ` · +$${info.fee}` : ""}</strong>
        </div>
      ) : null}
      {info.exact ? (
        <div className="evm-soft-box">
          <span>Dirección exacta</span>
          <strong>{info.exact}</strong>
        </div>
      ) : info.isDelivery ? (
        <p className="evm-warning-copy">Esta reserva es a domicilio, pero no tiene dirección exacta guardada.</p>
      ) : null}
    </section>
  );
}

function DetailRow({ label, value, strong = false }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="evm-detail-row">
      <span>{label}</span>
      <strong className={strong ? "is-strong" : ""}>{value}</strong>
    </div>
  );
}

function eventModalStatusInfo(statusValue) {
  const raw = normalizeStatus(statusValue || "confirmed");
  if (raw === "confirmed") return { label: "Confirmada", tone: "confirmed" };
  if (raw === "pending") return { label: "Pendiente", tone: "pending" };
  if (raw === "in_progress") return { label: "En progreso", tone: "progress" };
  if (raw === "completed") return { label: "Completada", tone: "completed" };
  if (raw === "no_show") return { label: "No asistió", tone: "warning" };
  if (raw === "rejected") return { label: "Rechazada", tone: "cancelled" };
  if (raw === "cancelled") return { label: "Cancelada", tone: "cancelled" };
  return { label: raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "Reserva", tone: "neutral" };
}

function HistoryRail({ clientHistory }) {
  if (!clientHistory) return null;
  const recentCount = Array.isArray(clientHistory.recent) ? clientHistory.recent.length : 0;
  return (
    <aside className="evm-history-card evm-history-card-compact" aria-label="Historial del cliente">
      <div className="evm-history-head">
        <span>Cliente</span>
        <strong>{clientHistory.customerName || "Cliente"}</strong>
        <small>{clientHistory.lastVisitLabel || "Sin visita anterior"} · {clientHistory.lastVisitSub || "Primera reserva visible en Loop."}</small>
      </div>

      <div className="evm-history-tags">
        <span className={`evm-history-tag ${clientHistory.segmentTone || "new"}`}>{clientHistory.segment || "Nuevo"}</span>
        <span className={`evm-history-tag ${clientHistory.riskTone || "neutral"}`}>{clientHistory.risk || "Sin historial"}</span>
        {clientHistory.averageTicketLabel ? <span className="evm-history-tag neutral">Ticket prom. {clientHistory.averageTicketLabel}</span> : null}
      </div>

      <div className="evm-history-stats">
        <div><span>Reservas</span><strong>{clientHistory.totalVisits || 0}</strong></div>
        <div><span>Completadas</span><strong>{clientHistory.completed || 0}</strong></div>
        <div><span>No asistió</span><strong>{clientHistory.noShow || 0}</strong></div>
        <div><span>Total</span><strong>{clientHistory.totalSpentLabel || "$0"}</strong></div>
      </div>

      {clientHistory.nextAction ? (
        <div className="evm-history-next-action">
          <span>Siguiente mejor acción</span>
          <strong>{clientHistory.nextAction}</strong>
        </div>
      ) : null}

      <details className="evm-compact-details">
        <summary><span>Ver historial del cliente</span><em>{recentCount ? `${recentCount} recientes` : "Sin visitas"}</em></summary>
        <div className="evm-history-body">
          <div className="evm-history-box">
            <span>Servicio más frecuente</span>
            <strong>{clientHistory.favoriteService || "—"}</strong>
            <small>{clientHistory.upcoming ? `${clientHistory.upcoming} próxima reserva` : "Sin próximas reservas visibles"}</small>
          </div>
          <div className="evm-history-box is-recent">
            {Array.isArray(clientHistory.recent) && clientHistory.recent.length ? clientHistory.recent.map((item) => (
              <div key={item.id}>
                <strong>{item.dateLabel}</strong>
                <span>{item.service} · {item.status}{item.price ? ` · ${money(item.price)}` : ""}</span>
              </div>
            )) : <div><strong>Primera vez</strong><span>No hay visitas anteriores visibles.</span></div>}
          </div>
          {Array.isArray(clientHistory.notes) && clientHistory.notes.length ? (
            <div className="evm-history-notes">
              <span>Notas recientes</span>
              {clientHistory.notes.map((note, idx) => <em key={`${idx}-${note}`}>{note}</em>)}
            </div>
          ) : null}
        </div>
      </details>
    </aside>
  );
}


function evmDateInputValue(date) {
  const d = date ? new Date(date) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function evmTimeInputValue(date) {
  const d = date ? new Date(date) : new Date();
  if (Number.isNaN(d.getTime())) return "09:00";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function evmBuildDateTime(dateValue, timeValue) {
  const dm = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const tm = String(timeValue || "09:00").match(/^(\d{1,2}):(\d{2})$/);
  const d = dm ? new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3])) : new Date();
  d.setHours(tm ? Number(tm[1]) : 9, tm ? Number(tm[2]) : 0, 0, 0);
  return d;
}

function evmBookingDurationMinutes(raw = {}) {
  const n = Number(raw.durationMinutes ?? raw.durationMins ?? raw.duration ?? raw.minutes ?? raw.visitMinutes ?? 60);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 60;
}

function evmWorkerLabel(worker) {
  return worker?.name || worker?.displayName || worker?.employeeName || worker?.email || "Trabajador";
}

function evmRawFromEventLike(item) {
  const extended = item?.extendedProps || {};
  return extended.raw || extended.booking || item?.raw || item?.booking || item || {};
}

function evmBookingId(raw = {}, fallback = "") {
  return String(raw.id || raw.bookingId || raw.docId || raw.uid || fallback || "").trim();
}

function evmCustomerPhone(raw = {}) {
  return String(raw.customerPhone || raw.clientPhone || raw.phone || raw.whatsapp || raw.whatsApp || raw.customerSnapshot?.phone || raw.customerSnapshot?.phoneNormalized || raw.customer?.phone || "").trim();
}

function evmWhatsAppOptOut(raw = {}) {
  const snapshot = raw.customerSnapshot && typeof raw.customerSnapshot === "object" ? raw.customerSnapshot : {};
  const customer = raw.customer && typeof raw.customer === "object" ? raw.customer : {};
  return raw.customerWhatsappOptOut === true || raw.whatsappOptOut === true || raw.whatsappOptIn === false || raw.customerWhatsappConsent === false || snapshot.whatsappOptOut === true || snapshot.whatsappOptIn === false || snapshot.customerWhatsappConsent === false || customer.whatsappOptOut === true || customer.whatsappOptIn === false;
}

function evmWhatsAppReadiness(raw = {}) {
  if (evmWhatsAppOptOut(raw)) return { label: "Cliente no acepta WhatsApp", tone: "is-error" };
  const digits = evmCustomerPhone(raw).replace(/\D/g, "");
  if (!digits) return { label: "Sin WhatsApp", tone: "is-warning" };
  if (digits.length < 8) return { label: "Teléfono inválido", tone: "is-error" };
  return { label: "WhatsApp listo", tone: "is-ok" };
}

function evmEventStart(raw = {}, item = null) {
  try {
    const byUtil = bookingScheduledAtToDate({ ...raw, start: item?.start || raw.start });
    if (byUtil && !Number.isNaN(byUtil.getTime())) return byUtil;
  } catch (_) {}
  const candidates = [item?.start, raw.start, raw.startAt, raw.scheduledAt, raw.dateTime, raw.date];
  for (const value of candidates) {
    try {
      if (!value) continue;
      const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
      if (!Number.isNaN(d.getTime())) return d;
    } catch (_) {}
  }
  try {
    const date = String(raw.date || "").trim();
    const time = /^\d{1,2}:\d{2}$/.test(String(raw.time || "")) ? String(raw.time) : "00:00";
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const d = new Date(`${date}T${time}:00`);
      if (!Number.isNaN(d.getTime())) return d;
    }
  } catch (_) {}
  return null;
}

function evmEventEnd(raw = {}, item = null, start = null) {
  const candidates = [item?.end, raw.end, raw.endAt, raw.endsAt];
  for (const value of candidates) {
    try {
      if (!value) continue;
      const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
      if (!Number.isNaN(d.getTime())) return d;
    } catch (_) {}
  }
  if (!start) return null;
  const end = new Date(start);
  end.setMinutes(end.getMinutes() + evmBookingDurationMinutes(raw));
  return end;
}

function evmResourceIds(raw = {}, item = null) {
  const ids = new Set();
  [raw.resourceId, raw.employeeId, raw.workerId, raw.assignedEmployeeId, item?.resourceId, item?.extendedProps?.resourceId, item?._def?.resourceIds?.[0]].forEach((id) => {
    if (id) ids.add(String(id));
  });
  if (Array.isArray(raw.assignedEmployeeIds)) raw.assignedEmployeeIds.forEach((id) => id && ids.add(String(id)));
  if (Array.isArray(raw.assignedEmployees)) raw.assignedEmployees.forEach((emp) => {
    const id = emp?.id || emp?.employeeId || emp?.uid;
    if (id) ids.add(String(id));
  });
  return ids;
}

function evmNormalizeExistingBookings(items = [], source = "events") {
  return (Array.isArray(items) ? items : []).map((item, idx) => {
    const raw = evmRawFromEventLike(item);
    const start = evmEventStart(raw, item);
    const end = evmEventEnd(raw, item, start);
    if (!start || !end) return null;
    return {
      id: evmBookingId(raw, item?.id || `${source}-${idx}`),
      start,
      end,
      resourceIds: evmResourceIds(raw, item),
      customerName: raw.customerName || raw.clientName || raw.name || "Cliente",
      title: raw.serviceName || raw.service || raw.title || item?.title || "Reserva",
      raw,
    };
  }).filter(Boolean);
}

function evmOverlaps(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart < bEnd && aEnd > bStart;
}

export default function EventViewModal({
  open,
  onClose,
  booking,
  employees = [],
  services = [],
  businessId,
  onAccept,
  onReject,
  onCancelBooking,
  onRemoveFromAgenda,
  onEdit,
  onReschedule,
  existingEvents = [],
  existingBookings = [],
  rescheduleSaving = false,
  onStatusChange,
  onRefresh,
  onPaymentSave,
  onProductSalesSave,
  paymentSaving = false,
  productSalesSaving = false,
  statusWorkflowSaving = false,
  statusFlowItems = [],
  clientHistory = null,
  permissions = null,
  isOwnerOrAdmin = false,
  businessSettings = null,
}) {
  if (!open || !booking) return null;

  const schedDate = bookingScheduledAtToDate(booking);
  const service = services.find((s) => String(s.id) === String(booking.serviceId)) || { id: booking.serviceId, name: booking.serviceName };
  const worker = employees.find((e) => String(e.id) === String(booking.employeeId)) || null;
  const status = normalizeStatus(booking.status || booking.confirmationStatus);
  const statusInfo = eventModalStatusInfo(booking.status || booking.confirmationStatus);
  const membershipCreditManualOnlyNotice = (booking?.membershipApplied === true || booking?.membershipBenefitApplied === true)
    && ["cancelled", "rejected", "no_show"].includes(status)
    && (String(booking?.membershipBenefitType || "").toLowerCase() === "credit" || Number(booking?.membershipCreditsUsed || 0) > 0)
      ? `Esta reserva usó ${Math.max(1, Number(booking?.membershipCreditsUsed || 1))} crédito de membresía. Cancelarla/no-show no restaura el crédito automáticamente; ajuste manual desde CRM si aplica.`
      : "";
  const membershipSummary = evmMembershipSummary(booking);
  const eventDetails = getEventDetails(booking);
  const locationInfo = getBookingLocationInfo(booking);

  const perms = normalizeWorkerPermissions(permissions);
  const loopSettings = normalizeLoopBusinessSettings(businessSettings || {});
  const inventoryModuleEnabled = loopSettings.enabledModules.inventory !== false && loopSettings.inventorySettings.enabled !== false && loopSettings.inventorySettings.plan !== "disabled";
  // LOOP PHASE 2.0.4 — permission enforcement inside reservation detail with module settings.
  // Agenda permissions, financial visibility, commissions and inventory are intentionally separated.
  const canViewFinancials = isOwnerOrAdmin || perms.canViewFinancials;
  const canViewPaymentDetails = isOwnerOrAdmin || perms.canViewFinancials || perms.canViewPaymentMethods || perms.canEditPayments;
  const canViewCommissions = Boolean(loopSettings.financeSettings?.showWorkerPayouts !== false) && (isOwnerOrAdmin || perms.canViewCommissions);
  const canViewInventory = inventoryModuleEnabled && (isOwnerOrAdmin || perms.canViewInventory);
  const canViewInventoryCosts = inventoryModuleEnabled && (isOwnerOrAdmin || perms.canViewInventoryCosts || perms.canViewFinancials);
  const canViewPrices = isOwnerOrAdmin || perms.canViewPrices || canViewFinancials;
  const canViewPhone = isOwnerOrAdmin || perms.canViewCustomerPhone;
  const canViewEmail = isOwnerOrAdmin || perms.canViewCustomerEmail;
  const canAccept = isOwnerOrAdmin || perms.canAcceptBookings;
  const canReject = isOwnerOrAdmin || perms.canRejectBookings;
  const canCancel = isOwnerOrAdmin || perms.canCancelBookings;
  const canComplete = isOwnerOrAdmin || perms.canCompleteBookings;
  const canEdit = isOwnerOrAdmin || perms.canEditBookings;
  const loyaltyRewardApplied = Boolean(
    booking?.loyaltyRewardApplied === true ||
    String(booking?.paymentMethod || booking?.payment?.method || booking?.paymentCoverageType || booking?.payment?.coverageType || "").toLowerCase() === "loyalty_reward" ||
    booking?.loyaltyRedemptionEventId ||
    booking?.loyaltyRedemptionToken
  );
  const loyaltyRewardAlreadyReversed = Boolean(booking?.loyaltyRewardReversed === true || booking?.loyaltyRedemptionReversed === true);
  const loyaltyWorkerPayoutPaid = ["paid", "pagado", "paid_worker", "worker_paid"].includes(String(
    booking?.loyaltyWorkerPayoutStatus ||
    booking?.workerPayoutStatus ||
    booking?.payment?.workerPayoutStatus ||
    ""
  ).toLowerCase());
  const canReverseLoyalty = Boolean(canEdit && loyaltyRewardApplied && !loyaltyRewardAlreadyReversed && !loyaltyWorkerPayoutPaid);
  const showPendingButtons = status === "pending";
  const canEditPayment = (isOwnerOrAdmin || perms.canEditPayments) && typeof onPaymentSave === "function";
  const bookingTotalForPayment = moneyNumber(booking.totalPrice ?? booking.total ?? booking.amount ?? eventDetails.totalPrice ?? booking.servicePrice ?? 0);
  const [paymentDraft, setPaymentDraft] = useState({
    paymentStatus: booking.paymentStatus || booking.payment?.status || "pending",
    paymentMethod: booking.paymentMethod || booking.payment?.method || "cash",
    amountCharged: String(booking.amountCharged ?? booking.paidAmount ?? booking.paymentAmount ?? ""),
    discountAmount: String(booking.discountAmount ?? booking.discount ?? ""),
    tipAmount: String(booking.tipAmount ?? booking.tip ?? ""),
    workerCommissionType: normalizeCommissionType(booking.workerCommissionType || booking.commissionType || booking.payment?.workerCommissionType || booking.payment?.commissionType || "none"),
    workerCommissionValue: String(booking.workerCommissionValue ?? booking.commissionValue ?? booking.payment?.workerCommissionValue ?? booking.payment?.commissionValue ?? ""),
    commissionIncludesTips: Boolean(booking.commissionIncludesTips || booking.payment?.commissionIncludesTips),
    commissionOnlyCompleted: booking.commissionOnlyCompleted !== false,
    workerPayoutStatus: booking.workerPayoutStatus || booking.payment?.workerPayoutStatus || "pending",
    workerPaymentNotes: String(booking.workerPaymentNotes || booking.payment?.workerPaymentNotes || ""),
  });
  const [inventoryItems, setInventoryItems] = useState([]);
  const [productSalesDraft, setProductSalesDraft] = useState(() => normalizeRetailRows(booking.productSales || booking.retailSales || []));
  const [whatsappManualSending, setWhatsappManualSending] = useState(false);
  const [whatsappManualStatus, setWhatsappManualStatus] = useState("");
  const [loyaltyReprocessSending, setLoyaltyReprocessSending] = useState(false);
  const [loyaltyReprocessStatus, setLoyaltyReprocessStatus] = useState("");
  const [loyaltyReverseSending, setLoyaltyReverseSending] = useState(false);
  const [loyaltyReverseStatus, setLoyaltyReverseStatus] = useState("");
  const [membershipBenefitSending, setMembershipBenefitSending] = useState(false);
  const [membershipBenefitStatus, setMembershipBenefitStatus] = useState("");
  const [wompiSessionLoading, setWompiSessionLoading] = useState(false);
  const [wompiSessionStatus, setWompiSessionStatus] = useState("");
  const [wompiSession, setWompiSession] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDraft, setRescheduleDraft] = useState(() => {
    const start = bookingScheduledAtToDate(booking) || new Date();
    return {
      date: evmDateInputValue(start),
      time: evmTimeInputValue(start),
      duration: String(evmBookingDurationMinutes(booking)),
      employeeId: String(booking.employeeId || booking.workerId || booking.resourceId || ""),
    };
  });
  const [lifecyclePrompt, setLifecyclePrompt] = useState(null);
  const [financePanelOpen, setFinancePanelOpen] = useState(false);

  useEffect(() => {
    setPaymentDraft({
      paymentStatus: booking.paymentStatus || booking.payment?.status || "pending",
      paymentMethod: booking.paymentMethod || booking.payment?.method || "cash",
      amountCharged: String(booking.amountCharged ?? booking.paidAmount ?? booking.paymentAmount ?? booking.payment?.amountCharged ?? ""),
      discountAmount: String(booking.discountAmount ?? booking.discount ?? booking.payment?.discountAmount ?? ""),
      tipAmount: String(booking.tipAmount ?? booking.tip ?? booking.payment?.tipAmount ?? ""),
      workerCommissionType: normalizeCommissionType(booking.workerCommissionType || booking.commissionType || booking.payment?.workerCommissionType || booking.payment?.commissionType || "none"),
      workerCommissionValue: String(booking.workerCommissionValue ?? booking.commissionValue ?? booking.payment?.workerCommissionValue ?? booking.payment?.commissionValue ?? ""),
      commissionIncludesTips: Boolean(booking.commissionIncludesTips || booking.payment?.commissionIncludesTips),
      commissionOnlyCompleted: booking.commissionOnlyCompleted !== false,
      workerPayoutStatus: booking.workerPayoutStatus || booking.payment?.workerPayoutStatus || "pending",
      workerPaymentNotes: String(booking.workerPaymentNotes || booking.payment?.workerPaymentNotes || ""),
    });
  }, [booking?.id, open]);

  useEffect(() => {
    setProductSalesDraft(normalizeRetailRows(booking.productSales || booking.retailSales || []));
  }, [booking?.id, open]);

  useEffect(() => {
    const start = bookingScheduledAtToDate(booking) || new Date();
    setRescheduleDraft({
      date: evmDateInputValue(start),
      time: evmTimeInputValue(start),
      duration: String(evmBookingDurationMinutes(booking)),
      employeeId: String(booking.employeeId || booking.workerId || booking.resourceId || ""),
    });
    setRescheduleOpen(false);
    setFinancePanelOpen(false);
  }, [booking?.id, open]);

  useEffect(() => {
    let alive = true;
    async function loadInventoryItems() {
      if (!open || !businessId) { if (alive) setInventoryItems([]); return; }
      try {
        const snap = await getDocs(collection(db, `businesses/${businessId}/inventoryItems`));
        if (!alive) return;
        const rows = [];
        snap.forEach((docSnap) => rows.push({ id: docSnap.id, ...(docSnap.data() || {}) }));
        setInventoryItems(rows.sort((a, b) => String(a.name || a.title || "").localeCompare(String(b.name || b.title || ""))));
      } catch (err) {
        console.warn("[EventViewModal] inventory load skipped", err);
        if (alive) setInventoryItems([]);
      }
    }
    loadInventoryItems();
    return () => { alive = false; };
  }, [open, businessId]);

  const paymentPreview = useMemo(() => {
    const tip = moneyNumber(paymentDraft.tipAmount, 0);
    const discount = moneyNumber(paymentDraft.discountAmount, 0);
    // Cobrado representa solo servicio/base cobrada; propina es separada.
    const charged = moneyNumber(paymentDraft.amountCharged, paymentDraft.paymentStatus === "paid" ? Math.max(0, bookingTotalForPayment - discount) : 0);
    const commissionValue = moneyNumber(paymentDraft.workerCommissionValue, 0);

    // HOTFIX PRODUCCIÓN: la comisión se calcula sobre el monto realmente cobrado.
    // Si Cobrado cambia manualmente, el payout debe recalcularse con esa base.
    // Si Cobrado está vacío, usamos el total menos descuento como fallback.
    // La propina no entra en la base; se suma completa al trabajador.
    const fallbackCommissionBase = Math.max(0, bookingTotalForPayment - discount);
    const commissionBase = Math.max(0, charged > 0 ? charged : fallbackCommissionBase);
    const commissionPayout = paymentDraft.workerCommissionType === "percentage"
      ? Math.max(0, commissionBase * (commissionValue / 100))
      : paymentDraft.workerCommissionType === "fixed"
        ? Math.max(0, commissionValue)
        : 0;
    const tipPayout = Math.max(0, tip);
    const payout = Math.max(0, commissionPayout + tipPayout);
    const totalCollected = Math.max(0, charged + tip);
    return { charged, totalCollected, commissionBase, commissionPayout, tipPayout, payout, net: Math.max(0, totalCollected - payout) };
  }, [paymentDraft, bookingTotalForPayment]);

  const existingProductSalesTotal = moneyNumber(booking.productSalesTotalAmount ?? booking.retailSalesTotalAmount ?? retailRowsTotal(productSalesDraft), 0);
  const productSalesDraftTotal = retailRowsTotal(productSalesDraft);
  const productSalesDraftPayout = retailRowsPayout(productSalesDraft);
  const allowNegativeProductStock = Boolean(loopSettings.inventorySettings?.allowNegativeStock);
  const productStockDeductTrigger = loopSettings.inventorySettings?.deductTrigger || "completed";
  const getProductStock = (itemOrRow = {}) => {
    const raw = itemOrRow.currentStock ?? itemOrRow.stock ?? itemOrRow.quantityAvailable ?? itemOrRow.availableStockAtSale ?? null;
    if (raw === null || raw === undefined || raw === "") return null;
    const n = moneyNumber(raw, NaN);
    return Number.isFinite(n) ? n : null;
  };
  const productSalesStockIssues = (Array.isArray(productSalesDraft) ? productSalesDraft : []).filter((row) => {
    if (allowNegativeProductStock) return false;
    const item = inventoryItems.find((it) => String(it.id) === String(row.itemId)) || {};
    const stock = getProductStock({ ...item, availableStockAtSale: row.availableStockAtSale });
    return stock !== null && moneyNumber(row.quantity, 0) > stock;
  });
  const productSalesDeductionHint = loopSettings.inventorySettings?.autoDeduct === false
    ? "No se descontará stock automáticamente."
    : productStockDeductTrigger === "paid"
      ? "Se descontará al marcar como cobrada."
      : "Se descontará al completar la reserva.";
  const serviceMaterialRows = useMemo(() => {
    const actual = Array.isArray(booking.inventoryConsumed) ? booking.inventoryConsumed : [];
    if (actual.length) return actual.map((row) => ({ ...row, estimated: false }));
    return getServiceInventoryUsageRows(service, inventoryItems);
  }, [booking?.id, booking?.inventoryConsumed, service?.id, inventoryItems]);
  const serviceMaterialCost = serviceMaterialRows.reduce((sum, row) => sum + moneyNumber(row?.totalCost ?? (moneyNumber(row?.quantityUsed, 0) * moneyNumber(row?.unitCost, 0)), 0), 0);
  const canEditProductSales = inventoryModuleEnabled && (isOwnerOrAdmin || perms.canEditPayments || perms.canEditInventory || perms.canAdjustInventory) && typeof onProductSalesSave === "function";
  const productSalesCanSave = canEditProductSales && productSalesStockIssues.length === 0;

  const canReschedule = canEdit && typeof onReschedule === "function";
  const rescheduleStart = useMemo(() => evmBuildDateTime(rescheduleDraft.date, rescheduleDraft.time), [rescheduleDraft.date, rescheduleDraft.time]);
  const rescheduleDuration = Math.max(15, Number(rescheduleDraft.duration || 60) || 60);
  const rescheduleEnd = useMemo(() => {
    const end = new Date(rescheduleStart);
    end.setMinutes(end.getMinutes() + rescheduleDuration);
    return end;
  }, [rescheduleStart, rescheduleDuration]);
  const rescheduleWorker = employees.find((emp) => String(emp.id) === String(rescheduleDraft.employeeId || "")) || null;
  const rescheduleConflicts = useMemo(() => {
    const currentId = evmBookingId(booking);
    const rows = [
      ...evmNormalizeExistingBookings(existingEvents, "events"),
      ...evmNormalizeExistingBookings(existingBookings, "bookings"),
    ];
    const seen = new Set();
    return rows.filter((row) => {
      const key = row.id || `${row.start?.getTime()}-${row.customerName}-${row.title}`;
      if (seen.has(key)) return false;
      seen.add(key);
      if (currentId && row.id && String(row.id) === String(currentId)) return false;
      const targetWorkerId = String(rescheduleDraft.employeeId || "");
      if (targetWorkerId && row.resourceIds && row.resourceIds.size && !row.resourceIds.has(targetWorkerId)) return false;
      return evmOverlaps(rescheduleStart, rescheduleEnd, row.start, row.end);
    }).slice(0, 4);
  }, [booking, existingEvents, existingBookings, rescheduleDraft.employeeId, rescheduleStart, rescheduleEnd]);

  const updateRescheduleDraft = (key, value) => setRescheduleDraft((prev) => ({ ...(prev || {}), [key]: value }));
  const saveReschedule = () => {
    if (!canReschedule) return;
    onReschedule({
      date: rescheduleDraft.date,
      time: rescheduleDraft.time,
      duration: rescheduleDuration,
      employeeId: rescheduleDraft.employeeId || null,
      start: rescheduleStart,
      end: rescheduleEnd,
      conflictCount: rescheduleConflicts.length,
    });
  };

  const getPaymentAutofillAmount = () => {
    const discount = moneyNumber(paymentDraft.discountAmount, 0);
    const fallback = Math.max(0, bookingTotalForPayment - discount);
    return fallback > 0 ? formatMoneyInputValue(fallback) : "";
  };

  const setPaymentField = (key, value) => setPaymentDraft((prev) => {
    const next = { ...prev, [key]: value };
    const coveredMethods = new Set(["membership", "loyalty_reward", "courtesy"]);
    if (key === "paymentStatus" && value === "paid" && !String(prev.amountCharged || "").trim()) {
      const autofill = getPaymentAutofillAmount();
      if (autofill) next.amountCharged = autofill;
    }
    if (key === "paymentStatus" && value === "covered") {
      next.amountCharged = "";
      if (!coveredMethods.has(String(prev.paymentMethod || "").toLowerCase())) next.paymentMethod = "membership";
    }
    if (key === "paymentMethod" && coveredMethods.has(String(value || "").toLowerCase())) {
      next.paymentStatus = "covered";
      next.amountCharged = "";
    }
    return next;
  });

  const currentCommissionLifecycle = evmCommissionLifecycleStatus({
    bookingStatus: status,
    paymentStatus: paymentDraft.paymentStatus,
    commissionOnlyCompleted: paymentDraft.commissionOnlyCompleted !== false,
  });

  const paymentRequiresAmount = ["paid", "partial"].includes(String(paymentDraft.paymentStatus || "").toLowerCase()) && paymentPreview.charged <= 0;
  const paidCanSuggestCompletion = canComplete && String(paymentDraft.paymentStatus || "").toLowerCase() === "paid" && !evmIsClosedStatus(status) && typeof onStatusChange === "function";

  const buildPaymentPayload = () => {
    const commissionLifecycleStatus = evmCommissionLifecycleStatus({
      bookingStatus: status,
      paymentStatus: paymentDraft.paymentStatus,
      commissionOnlyCompleted: paymentDraft.commissionOnlyCompleted !== false,
    });
    const payload = {
      paymentStatus: paymentDraft.paymentStatus,
      paymentMethod: paymentDraft.paymentMethod,
      amountCharged: paymentPreview.charged,
      paidAmount: paymentPreview.charged,
      balanceAmount: Math.max(0, Math.round((bookingTotalForPayment - moneyNumber(paymentDraft.discountAmount, 0) - paymentPreview.charged) * 100) / 100),
      discountAmount: moneyNumber(paymentDraft.discountAmount, 0),
      tipAmount: moneyNumber(paymentDraft.tipAmount, 0),
      totalCollectedAmount: paymentPreview.totalCollected,
      grossCollectedAmount: paymentPreview.totalCollected,
      serviceCollectedAmount: paymentPreview.charged,
      workerCommissionType: paymentDraft.workerCommissionType,
      workerCommissionValue: moneyNumber(paymentDraft.workerCommissionValue, 0),
      commissionBaseAmount: paymentPreview.commissionBase,
      commissionIncludesTips: false,
      commissionOnlyCompleted: paymentDraft.commissionOnlyCompleted !== false,
      commissionLifecycleStatus,
      commissionLifecycleLabel: evmCommissionLifecycleLabel(commissionLifecycleStatus),
      workerPayoutAmount: paymentPreview.payout,
      workerPayoutStatus: paymentDraft.workerPayoutStatus || "pending",
      workerPaymentNotes: String(paymentDraft.workerPaymentNotes || "").trim() || null,
      businessNetAmount: paymentPreview.net,
      payment: {
        status: paymentDraft.paymentStatus,
        method: paymentDraft.paymentMethod,
        amountCharged: paymentPreview.charged,
        paidAmount: paymentPreview.charged,
        balanceAmount: Math.max(0, Math.round((bookingTotalForPayment - moneyNumber(paymentDraft.discountAmount, 0) - paymentPreview.charged) * 100) / 100),
        discountAmount: moneyNumber(paymentDraft.discountAmount, 0),
        tipAmount: moneyNumber(paymentDraft.tipAmount, 0),
        totalCollectedAmount: paymentPreview.totalCollected,
        grossCollectedAmount: paymentPreview.totalCollected,
        serviceCollectedAmount: paymentPreview.charged,
        workerCommissionType: paymentDraft.workerCommissionType,
        workerCommissionValue: moneyNumber(paymentDraft.workerCommissionValue, 0),
        commissionBaseAmount: paymentPreview.commissionBase,
        commissionIncludesTips: false,
        commissionOnlyCompleted: paymentDraft.commissionOnlyCompleted !== false,
        commissionLifecycleStatus,
        commissionLifecycleLabel: evmCommissionLifecycleLabel(commissionLifecycleStatus),
        workerPayoutAmount: paymentPreview.payout,
        workerPayoutStatus: paymentDraft.workerPayoutStatus || "pending",
        workerPaymentNotes: String(paymentDraft.workerPaymentNotes || "").trim() || null,
        businessNetAmount: paymentPreview.net,
      },
    };
    // LOOP PHASE 2.5D — send canonical payment/lifecycle aliases to the calendar updater.
    Object.assign(payload, buildBookingLifecyclePatch(booking, payload));
    payload.commissionLifecycleStatus = commissionLifecycleStatus;
    payload.commissionLifecycleLabel = evmCommissionLifecycleLabel(commissionLifecycleStatus);
    payload.payment = { ...(payload.payment || {}), commissionLifecycleStatus, commissionLifecycleLabel: evmCommissionLifecycleLabel(commissionLifecycleStatus) };
    return payload;
  };

  const savePayment = async (options = {}) => {
    if (!canEditPayment) return;
    if (paymentRequiresAmount) {
      alert("Para marcar esta reserva como pagada o parcial, ingresa el monto cobrado.");
      return;
    }
    if (!options.skipCompletionPrompt && paidCanSuggestCompletion) {
      setLifecyclePrompt({ type: "paid_suggest_complete" });
      return;
    }
    const payload = buildPaymentPayload();
    await Promise.resolve(onPaymentSave(payload));
    if (options.completeAfter && canComplete && typeof onStatusChange === "function") {
      await Promise.resolve(onStatusChange("completed"));
    }
  };

  const requestStatusChange = (nextStatus) => {
    if (evmStatusNeedsPaymentDecision(nextStatus, paymentDraft.paymentStatus, paymentPreview.charged)) {
      setLifecyclePrompt({ type: "complete_without_payment", nextStatus });
      return;
    }
    if (normalizeStatus(nextStatus) === "completed" && !canComplete) return;
    onStatusChange(nextStatus);
  };

  const preparePaymentForCompletion = () => {
    const autofill = getPaymentAutofillAmount();
    setPaymentDraft((prev) => ({
      ...prev,
      paymentStatus: "paid",
      paymentMethod: prev.paymentMethod || "cash",
      amountCharged: String(prev.amountCharged || autofill || ""),
    }));
    setLifecyclePrompt(null);
  };

  const updateProductSaleRow = (idx, key, value) => {
    setProductSalesDraft((prev) => (Array.isArray(prev) ? prev : []).map((row, rowIdx) => {
      if (rowIdx !== idx) return row;
      const next = { ...row, [key]: value };
      if (key === "itemId") {
        const item = inventoryItems.find((it) => String(it.id) === String(value)) || {};
        next.itemName = item.name || item.title || "Producto";
        next.unitPrice = String(item.salePrice ?? item.price ?? next.unitPrice ?? "");
        next.unitCost = String(item.unitCost ?? item.cost ?? next.unitCost ?? "");
        next.unit = item.unit || item.measureUnit || next.unit || "unidad";
        next.availableStockAtSale = getProductStock(item);
        next.trackStock = item.trackStock !== false;
      }
      return next;
    }));
  };

  const addProductSaleRow = () => {
    const commissionType = normalizeCommissionType(worker?.retailCommissionType || worker?.productCommissionType || paymentDraft.workerCommissionType || "none");
    const commissionValue = worker?.retailCommissionValue ?? worker?.productCommissionValue ?? paymentDraft.workerCommissionValue ?? "";
    setProductSalesDraft((prev) => [...(Array.isArray(prev) ? prev : []), { id: `retail-${Date.now()}`, itemId: "", itemName: "Producto", quantity: "1", unitPrice: "", unitCost: "", unit: "unidad", availableStockAtSale: null, trackStock: true, workerCommissionType: commissionType, workerCommissionValue: commissionType !== "none" ? String(commissionValue || "") : "" }]);
  };

  const removeProductSaleRow = (idx) => setProductSalesDraft((prev) => (Array.isArray(prev) ? prev : []).filter((_, rowIdx) => rowIdx !== idx));

  const saveProductSales = () => {
    if (!canEditProductSales) return;
    if (productSalesStockIssues.length) {
      alert("Hay productos con cantidad mayor al stock disponible. Ajusta la cantidad o activa stock negativo en Business OS.");
      return;
    }
    onProductSalesSave({ productSales: productSalesDraft, productSalesDeductTrigger: productStockDeductTrigger });
  };

  const workerText = eventDetails.isEvent && (booking.assignmentStatus === "not_required" || booking.employeeNotRequired === true || !booking.employeeId)
    ? "No requiere trabajador"
    : worker
      ? (worker.name || worker.displayName || worker.email)
      : (booking.employeeEmail || "Sin asignar");

  const characterNames = eventDetails.selectedCharacters.length
    ? eventDetails.selectedCharacters.map((c) => c.name || c.id).filter(Boolean).join(", ")
    : eventDetails.selectedCharacterIds.length
      ? eventDetails.selectedCharacterIds.join(", ")
      : "";

  const visibleStatusItems = (Array.isArray(statusFlowItems) ? statusFlowItems : [])
    .filter((item) => ["pending", "confirmed", "in_progress", "completed", "no_show", "cancelled"].includes(item.value))
    .filter((item) => !isApprovalFirstEvent(booking) || !['pending','confirmed'].includes(item.value))
    .filter((item) => !isApprovalFirstEvent(booking) || (booking.eventApprovalStatus === 'approved' && !booking.paymentReviewRequired && !['pending_payment','payment_expired','expired'].includes(status)) || !['in_progress','completed'].includes(item.value))
    .filter((item) => item.value !== "completed" || canComplete)
    .filter((item) => item.value !== "cancelled" || canCancel);

  const scheduledLabel = schedDate
    ? `${schedDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })} — ${schedDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : "";


  const customerName = booking.customerName || booking.clientName || booking.name || "Cliente";
  const customerPhone = canViewPhone ? String(booking.customerPhone || booking.clientPhone || booking.phone || "").trim() : "";
  const customerEmail = canViewEmail ? String(booking.customerEmail || booking.clientEmail || booking.email || "").trim() : "";
  const serviceLabel = booking.serviceName || service?.name || "Servicio";
  const shortDateLabel = schedDate
    ? schedDate.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" }).replace(".", "")
    : "Sin fecha";
  const shortTimeLabel = schedDate
    ? schedDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "Sin hora";
  const displayTotal = money(bookingTotalForPayment || eventDetails.totalPrice || booking.totalPrice || 0);
  const hasPaymentAdvanced = paymentDraft.workerCommissionType !== "none" || Number(paymentDraft.discountAmount || 0) > 0 || Number(paymentDraft.tipAmount || 0) > 0;
  const productSalesDisplayTotal = productSalesDraftTotal || existingProductSalesTotal;
  const compactPaymentLine = `${paymentMethodLabel(paymentDraft.paymentMethod)} · ${paymentStatusLabel(paymentDraft.paymentStatus)}`;
  const paymentOutstanding = Math.max(
    0,
    bookingTotalForPayment - moneyNumber(paymentDraft.discountAmount, 0) - paymentPreview.charged
  );
  const paymentHeadline = String(paymentDraft.paymentStatus || "pending").toLowerCase() === "covered"
    ? "Cubierto por beneficio"
    : paymentOutstanding > 0
      ? `${money(paymentOutstanding)} pendiente`
      : paymentPreview.charged > 0
        ? `${money(paymentPreview.charged)} cobrado`
        : compactPaymentLine;

  const bookingIdForWhatsApp = evmBookingId(booking);
  const phoneForWhatsApp = evmCustomerPhone(booking);
  const whatsappReadiness = evmWhatsAppReadiness(booking);
  const customerWhatsAppOptOut = evmWhatsAppOptOut(booking);
  const canManageWhatsAppMessages = Boolean(isOwnerOrAdmin || perms.canManageBusinessSettings || perms.canManageWorkers);
  const canSendManualWhatsApp = Boolean(businessId && bookingIdForWhatsApp && phoneForWhatsApp && canManageWhatsAppMessages && !customerWhatsAppOptOut);
  const paymentProvider = booking.paymentProvider || booking.payment?.provider || "wompi";
  const paymentReference = wompiSession?.reference || booking.paymentReference || booking.wompiReference || booking.payment?.reference || "";
  const paymentUrl = wompiSession?.checkoutUrl || booking.paymentUrl || booking.wompiCheckoutUrl || booking.payment?.checkoutUrl || "";
  const depositAmount = moneyNumber(booking.depositAmount ?? booking.requiredDepositAmount ?? booking.payment?.depositAmount, 0) || (bookingTotalForPayment > 0 ? Math.min(bookingTotalForPayment, Math.max(5, bookingTotalForPayment * 0.25)) : 0);
  const currentPaidAmount = moneyNumber(booking.paidAmount ?? booking.amountCharged ?? booking.payment?.paidAmount ?? booking.payment?.amountCharged, 0);
  const balanceAmount = moneyNumber(booking.balanceAmount ?? booking.payment?.balanceAmount, Math.max(0, bookingTotalForPayment - currentPaidAmount));
  const canCreateWompiPayment = Boolean(canEditPayment && businessId && bookingIdForWhatsApp && bookingTotalForPayment > 0);

  const loadWompiWidgetScript = () => new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("Widget no disponible fuera del navegador"));
    if (window.WidgetCheckout) return resolve(window.WidgetCheckout);
    const existing = document.querySelector('script[data-loop-wompi-widget="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.WidgetCheckout));
      existing.addEventListener("error", () => reject(new Error("No se pudo cargar el widget de Wompi")));
      return;
    }
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "https://checkout.wompi.pa/widget.js";
    script.async = true;
    script.setAttribute("data-loop-wompi-widget", "true");
    script.onload = () => window.WidgetCheckout ? resolve(window.WidgetCheckout) : reject(new Error("WidgetCheckout no quedó disponible"));
    script.onerror = () => reject(new Error("No se pudo cargar el widget de Wompi"));
    document.head.appendChild(script);
  });

  const handleOpenWompiWidget = async () => {
    const session = wompiSession;
    const widget = session?.widget || null;
    if (!widget || !widget.publicKey || !widget.reference || !widget.signatureIntegrity) {
      setWompiSessionStatus("Primero prepara un checkout de abono o pago total.");
      return;
    }
    setWompiSessionLoading(true);
    setWompiSessionStatus("Abriendo widget seguro de Wompi...");
    try {
      const WidgetCheckout = await loadWompiWidgetScript();
      const checkout = new WidgetCheckout({
        currency: widget.currency || "USD",
        amountInCents: Number(widget.amountInCents || session.amountInCents || 0),
        reference: widget.reference,
        publicKey: widget.publicKey,
        signature: { integrity: widget.signatureIntegrity },
        redirectUrl: widget.redirectUrl,
        expirationTime: widget.expirationTime,
        defaultLanguage: widget.defaultLanguage || "es",
        customerData: widget.customerData || undefined,
      });
      checkout.open(async (result) => {
        const transaction = result?.transaction || {};
        const status = String(transaction.status || "").toUpperCase();
        setWompiSessionStatus(status ? `Wompi respondió: ${status}. Guardando resultado sandbox...` : "Wompi respondió. Esperando confirmación.");
        try {
          const app = db?.app || db?._app;
          const callable = httpsCallable(getFunctions(app), "syncWompiSandboxWidgetResult");
          const res = await callable({ businessId, bookingId: bookingIdForWhatsApp, transaction });
          setWompiSessionStatus(`Pago sandbox actualizado: ${res?.data?.status || "pendiente"}. El webhook real también podrá confirmar esta reserva.`);
        } catch (syncErr) {
          console.error("[Loop Payments] sync widget result failed", syncErr);
          setWompiSessionStatus("Wompi respondió, pero no pudimos sincronizar automáticamente. Revisa el webhook o refresca la reserva.");
        }
      });
    } catch (err) {
      console.error("[Loop Payments] open Wompi widget failed", err);
      setWompiSessionStatus(err?.message || "No pudimos abrir el widget de Wompi.");
    } finally {
      setWompiSessionLoading(false);
    }
  };

  const handleCreateWompiSession = async (mode = "deposit") => {
    if (!canCreateWompiPayment) return;
    setWompiSessionLoading(true);
    setWompiSessionStatus("");
    try {
      const app = db?.app || db?._app;
      const callable = httpsCallable(getFunctions(app), "createWompiPaymentSession");
      const res = await callable({ businessId, bookingId: bookingIdForWhatsApp, mode, amount: mode === "deposit" ? depositAmount : bookingTotalForPayment });
      setWompiSession(res?.data || null);
      setWompiSessionStatus("Checkout sandbox preparado. Ahora puedes abrir el widget real de Wompi dentro de Loop.");
    } catch (err) {
      console.error("[Loop Payments] create session failed", err);
      setWompiSessionStatus(err?.message || "No pudimos crear el checkout de pago.");
    } finally {
      setWompiSessionLoading(false);
    }
  };

  const handleMarkMockPaid = async () => {
    if (!businessId || !bookingIdForWhatsApp) return;
    setWompiSessionLoading(true);
    setWompiSessionStatus("");
    try {
      const app = db?.app || db?._app;
      const callable = httpsCallable(getFunctions(app), "markWompiPaymentMockPaid");
      const res = await callable({ businessId, bookingId: bookingIdForWhatsApp, reference: paymentReference, amount: wompiSession?.amount || depositAmount || bookingTotalForPayment });
      setWompiSessionStatus(`Pago mock registrado como ${res?.data?.status || "pagado"}. Refresca si no ves el cambio al instante.`);
    } catch (err) {
      console.error("[Loop Payments] mark mock paid failed", err);
      setWompiSessionStatus(err?.message || "No pudimos simular el pago.");
    } finally {
      setWompiSessionLoading(false);
    }
  };

  const handleSendManualWhatsApp = async () => {
    if (!businessId || !bookingIdForWhatsApp) {
      setWhatsappManualStatus("No encuentro el negocio o la reserva para enviar WhatsApp.");
      return;
    }
    if (!phoneForWhatsApp) {
      setWhatsappManualStatus("Esta reserva no tiene teléfono/WhatsApp del cliente.");
      return;
    }
    setWhatsappManualSending(true);
    setWhatsappManualStatus("");
    try {
      const app = db?.app || db?._app;
      const callable = httpsCallable(getFunctions(app), "sendBookingWhatsAppManual");
      const res = await callable({
        businessId,
        bookingId: bookingIdForWhatsApp,
      });
      const messageId = res?.data?.messageId || "";
      setWhatsappManualStatus(messageId ? "WhatsApp enviado correctamente." : "WhatsApp enviado.");
    } catch (err) {
      console.error("[Loop WhatsApp] manual send failed", err);
      setWhatsappManualStatus(evmWhatsAppErrorMessage(err));
    } finally {
      setWhatsappManualSending(false);
    }
  };


  const handleReprocessLoyalty = async () => {
    const bookingId = evmBookingId(booking);
    if (!businessId || !bookingId) {
      setLoyaltyReprocessStatus("Primero guarda la reserva antes de reprocesar fidelidad.");
      return;
    }
    setLoyaltyReprocessSending(true);
    setLoyaltyReprocessStatus("");
    try {
      const result = await reprocessBookingLoyaltyAction({ db, businessId, bookingId });
      setLoyaltyReprocessStatus(summarizeReprocessResult(result));
    } catch (err) {
      console.error("[EventViewModal] reprocess loyalty failed", err);
      const msg = err?.message || err?.details?.message || "No pude reprocesar fidelidad.";
      setLoyaltyReprocessStatus(`No pude reprocesar fidelidad: ${msg}`);
    } finally {
      setLoyaltyReprocessSending(false);
    }
  };

  const handleReverseLoyaltyRedemption = async () => {
    const bookingId = evmBookingId(booking);
    if (!businessId || !bookingId) {
      setLoyaltyReverseStatus("Primero guarda la reserva antes de deshacer fidelidad.");
      return;
    }

    if (loyaltyWorkerPayoutPaid) {
      setLoyaltyReverseStatus("No puedes deshacer fidelidad porque la comisión del trabajador ya fue marcada como pagada.");
      return;
    }

    const confirmed = typeof window === "undefined"
      ? true
      : window.confirm("¿Deshacer la redención de fidelidad? La recompensa volverá al cliente y la reserva quedará pendiente de pago.");
    if (!confirmed) return;

    const reason = typeof window === "undefined"
      ? "Corrección operativa"
      : window.prompt("Motivo del reverso", "Corrección operativa");
    if (reason === null) return;

    setLoyaltyReverseSending(true);
    setLoyaltyReverseStatus("");
    try {
      const result = await reverseLoyaltyRedemptionAction({
        db,
        businessId,
        bookingId,
        reason: String(reason || "Corrección operativa").trim(),
      });
      setLoyaltyReverseStatus(summarizeReverseLoyaltyRedemptionResult(result));
    } catch (err) {
      console.error("[EventViewModal] reverse loyalty redemption failed", err);
      const msg = err?.message || err?.details?.message || "No pude deshacer fidelidad.";
      setLoyaltyReverseStatus(`No pude deshacer fidelidad: ${msg}`);
    } finally {
      setLoyaltyReverseSending(false);
    }
  };

  const handleReprocessMembershipBenefit = async () => {
    const bookingId = evmBookingId(booking);
    if (!businessId || !bookingId) {
      setMembershipBenefitStatus("Primero guarda la reserva antes de procesar membresía.");
      return;
    }
    setMembershipBenefitSending(true);
    setMembershipBenefitStatus("");
    try {
      const result = await reprocessBookingMembershipBenefitAction({ businessId, bookingId });
      setMembershipBenefitStatus(summarizeMembershipBenefitResult(result));
    } catch (err) {
      console.error("[EventViewModal] reprocess membership benefit failed", err);
      const msg = err?.message || err?.details?.message || "No pude procesar membresía.";
      setMembershipBenefitStatus(`No pude procesar membresía: ${msg}`);
    } finally {
      setMembershipBenefitSending(false);
    }
  };

  return (
    <AccessibleModal open={open} onClose={onClose} labelledBy="event-modal-title" width={1060}>
      <style>{`${eventViewModalStyles}
.evm-retail-deduction-hint{display:flex;align-items:center;justify-content:space-between;gap:8px;border:1px dashed #cbd5e1;background:#f8fafc;border-radius:12px;padding:8px 10px;margin:8px 0;font-size:12px;font-weight:850;color:#64748b}
.evm-retail-deduction-hint strong,.evm-retail-stock-warning{color:#b45309}
.evm-retail-stock-warning{border:1px solid #fde68a;background:#fffbeb;border-radius:10px;padding:8px 10px;font-size:12px;font-weight:850}
.evm-lifecycle-nudge{display:flex;align-items:center;justify-content:space-between;gap:12px;border:1px solid #dbeafe;background:#f8fbff;border-radius:16px;padding:12px;margin:10px 0 2px}
.evm-lifecycle-nudge div{display:flex;flex-direction:column;gap:2px}.evm-lifecycle-nudge span{font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;color:#64748b}.evm-lifecycle-nudge strong{font-size:13px;font-weight:950;color:#0f172a}.evm-lifecycle-nudge em{font-size:12px;font-weight:850;color:#00675f;font-style:normal}.evm-lifecycle-nudge p{margin:0;font-size:12px;font-weight:850;color:#b45309}.evm-lifecycle-nudge button{border:0;background:#00675f;color:white;border-radius:999px;padding:9px 12px;font-size:12px;font-weight:950;white-space:nowrap}
.evm-lifecycle-prompt{position:absolute;inset:0;background:rgba(15,23,42,.34);z-index:60;display:grid;place-items:center;padding:18px}.evm-lifecycle-prompt-card{width:min(480px,100%);background:white;border:1px solid #e2e8f0;border-radius:24px;box-shadow:0 28px 80px rgba(15,23,42,.24);padding:18px}.evm-lifecycle-prompt-card span{font-size:11px;font-weight:950;color:#00675f;text-transform:uppercase;letter-spacing:.08em}.evm-lifecycle-prompt-card strong{display:block;font-size:20px;font-weight:950;color:#0f172a;margin-top:6px}.evm-lifecycle-prompt-card p{font-size:13px;font-weight:750;color:#64748b;line-height:1.45}.evm-lifecycle-prompt-actions{display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;margin-top:14px}.evm-lifecycle-prompt-actions button{border:1px solid #dbe4ef;background:#fff;border-radius:999px;padding:10px 13px;font-size:12px;font-weight:950}.evm-lifecycle-prompt-actions button.primary{border-color:#00675f;background:#00675f;color:white}.evm-whatsapp-status{font-size:12px;font-weight:850;color:#00675f;background:#f0fdfa;border:1px solid #99f6e4;border-radius:14px;padding:9px 11px}.evm-whatsapp-status.is-error{color:#b45309;background:#fffbeb;border-color:#fde68a}.evm-membership-policy-note{font-size:12px;font-weight:850;color:#92400e;background:#fffbeb;border:1px solid #fde68a;border-radius:14px;padding:9px 11px}.evm-membership-summary{display:flex;justify-content:space-between;gap:14px;align-items:center;border:1px solid #99f6e4;background:linear-gradient(135deg,#f0fdfa,#fff);border-radius:18px;padding:12px 13px}.evm-membership-summary span{display:block;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.10em;color:#00796f}.evm-membership-summary strong{display:block;margin-top:3px;color:#0f172a;font-size:14px;font-weight:950}.evm-membership-summary small{display:block;margin-top:3px;color:#64748b;font-size:12px;font-weight:800}.evm-membership-summary em{font-style:normal;border-radius:999px;background:#ccfbf1;color:#0f766e;padding:7px 10px;font-size:11px;font-weight:950;text-align:right;white-space:nowrap}.evm-membership-summary.is-pending em{background:#fffbeb;color:#92400e}.evm-membership-summary.is-review em{background:#fff7ed;color:#c2410c}
.evm-ops-strip{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:9px}.evm-ops-chip{border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:11px 12px;min-width:0}.evm-ops-chip span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}.evm-ops-chip strong{display:block;margin-top:4px;color:#0f172a;font-size:13px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.evm-ops-chip.is-ok,.evm-ops-chip.is-membership.is-processed{background:#ecfdf5;border-color:#99f6e4}.evm-ops-chip.is-warning,.evm-ops-chip.is-membership.is-review{background:#fffbeb;border-color:#fde68a}.evm-ops-chip.is-muted{background:#f8fafc}.evm-ops-chip.is-money,.evm-ops-chip.is-membership{background:#f0fdfa;border-color:#99f6e4}.evm-ops-chip.is-membership.is-pending{background:#fffbeb;border-color:#fde68a}.evm-compact-details{border:1px solid #e2e8f0;background:#fff;border-radius:18px;overflow:hidden}.evm-compact-details summary{list-style:none;cursor:pointer;padding:12px 13px;display:flex;align-items:center;justify-content:space-between;gap:12px}.evm-compact-details summary::-webkit-details-marker{display:none}.evm-compact-details summary span{color:#0f172a;font-size:13px;font-weight:950}.evm-compact-details summary em{font-style:normal;color:#64748b;font-size:11px;font-weight:900;white-space:nowrap}.evm-compact-details[open] summary{border-bottom:1px solid #eef2f7;background:#fbfdff}.evm-footer-detail-stack{display:grid;gap:9px;padding:12px}.evm-history-card-compact{max-height:none;overflow:visible}.evm-history-card-compact .evm-history-body{padding:12px}.evm-footer .evm-actions .secondary,.evm-footer .evm-actions .soft-ok{background:#fff}.evm-footer .evm-actions button:not(.primary){box-shadow:none}
`}</style>
      <div className="event-view-fit-modal evm-phase2">
        <header className="evm-header evm-header-minimal evm-header-v2">
          <div className="evm-header-main">
            <div className="evm-kicker">Gestión de reserva</div>
            <div className="evm-title-row">
              <h2 id="event-modal-title">{customerName} · {serviceLabel}</h2>
              <span className={`evm-status-pill is-${statusInfo.tone}`}>{statusInfo.label}</span>
            </div>
            <p>{shortDateLabel} · {shortTimeLabel} · {workerText}</p>
          </div>
          <div className="evm-header-actions evm-header-actions-v2">
            {canEdit ? <button onClick={onEdit} className="evm-edit-primary" type="button">Editar reserva</button> : null}
            {canEdit ? <button onClick={() => setRescheduleOpen(true)} className="evm-reprogram-btn" type="button">Reprogramar</button> : null}
            <button onClick={onClose} className="evm-close evm-close-icon" type="button" aria-label="Cerrar">×</button>
          </div>
        </header>

        <section className="evm-quickbar-v2" aria-label="Acciones rápidas de la reserva">
          <div className="evm-quickbar-item">
            <span>Estado</span>
            <strong>{statusInfo.label}</strong>
            <small>{scheduledLabel || "Sin fecha programada"}</small>
          </div>

          {canViewPaymentDetails || canViewCommissions ? (
            <button type="button" className={`evm-quickbar-item evm-quickbar-action is-payment-${String(paymentDraft.paymentStatus || "pending").toLowerCase()}`} onClick={() => setFinancePanelOpen(true)}>
              <div>
                <span>Cobro</span>
                <strong>{paymentHeadline}</strong>
                <small>{compactPaymentLine}</small>
              </div>
              <em>{canEditPayment ? "Gestionar" : "Ver"}</em>
            </button>
          ) : null}

          <div className={`evm-quickbar-item ${whatsappReadiness.tone === "is-ok" ? "is-ok" : whatsappReadiness.tone === "is-error" ? "is-warning" : ""}`}>
            <span>WhatsApp</span>
            <strong>{whatsappReadiness.label}</strong>
            <small>{customerPhone || "Sin teléfono visible"}</small>
          </div>

          {membershipSummary ? (
            <div className={`evm-quickbar-item is-membership is-${membershipSummary.state}`}>
              <span>Membresía</span>
              <strong>{membershipSummary.stateLabel}</strong>
              <small>{membershipSummary.planName}</small>
            </div>
          ) : null}
        </section>

        <div className="evm-scroll-area">
          <div className={`evm-layout ${clientHistory ? "has-history" : "no-history"}`}>
            <main className="evm-main-col">
              <section className="evm-command-card">
                <div className="evm-command-head">
                  <div className="evm-avatar">{String(customerName || "C").trim().slice(0, 1).toUpperCase()}</div>
                  <div>
                    <span>Cliente</span>
                    <strong>{customerName}</strong>
                    <small>{[customerPhone, customerEmail].filter(Boolean).join(" · ") || "Sin contacto visible"}</small>
                  </div>
                </div>

                <div className="evm-command-grid">
                  <div className="evm-mini-fact">
                    <span>Servicio</span>
                    <strong>{serviceLabel}</strong>
                    <small>{eventDetails.isEvent && eventDetails.visitHours ? `${eventDetails.visitHours} h` : (booking.duration || booking.durationMinutes ? `${booking.duration || booking.durationMinutes} min` : "Reserva")}</small>
                  </div>
                  <div className="evm-mini-fact">
                    <span>Trabajador</span>
                    <strong>{workerText}</strong>
                    <small>{eventDetails.isEvent ? "Equipo operativo" : "Asignación"}</small>
                  </div>
                  <div className="evm-mini-fact">
                    <span>Fecha y hora</span>
                    <strong>{shortDateLabel}</strong>
                    <small>{shortTimeLabel}</small>
                  </div>
                  {canViewPrices ? (
                    <div className="evm-mini-fact is-money">
                      <span>Total</span>
                      <strong>{displayTotal}</strong>
                      <small>{compactPaymentLine}</small>
                    </div>
                  ) : null}
                </div>
              </section>

              {booking.eventQuote && isOwnerOrAdmin ? <EventQuoteCard key={`${booking.id}-${booking.eventQuote.version}`} booking={booking} businessId={businessId} manager onUpdated={onRefresh}/> : null}
              {isOwnerOrAdmin ? <EventManualReceiptCard key={`${booking.id}-${booking.paidAmount}`} booking={booking} businessId={businessId} onUpdated={onRefresh}/> : null}
              {isOwnerOrAdmin ? <EventManualRefundCard key={`refund-${booking.id}`} booking={booking} businessId={businessId} onUpdated={onRefresh}/> : null}
              {isOwnerOrAdmin ? <EventPriceChangeCard key={`price-${booking.id}-${booking.eventPriceChange?.version}-${booking.eventPriceChange?.status}`} booking={booking} businessId={businessId} manager onUpdated={onRefresh}/> : null}
              <details className="evm-card evm-ux-disclosure evm-reservation-details">
                <summary>
                  <div>
                    <span>Detalles</span>
                    <strong>Información de la reserva</strong>
                  </div>
                  <em>Ver detalle</em>
                </summary>
                <div className="evm-ux-disclosure-body">
                  <div className="evm-snapshot-list">
                    <DetailRow label="Atención" value={locationInfo.modeLabel} />
                    <DetailRow label="Servicio" value={serviceLabel} />
                    {!eventDetails.isEvent ? <DetailRow label="Trabajador" value={workerText} /> : null}
                    {eventDetails.isEvent ? <DetailRow label="Tipo" value={eventDetails.visitTypeLabel || "Experiencia"} /> : null}
                    {eventDetails.isEvent && eventDetails.visitHours ? <DetailRow label="Duración" value={`${eventDetails.visitHours} ${Number(eventDetails.visitHours) === 1 ? "hora" : "horas"}`} /> : null}
                    {eventDetails.isEvent ? <>
                      {eventDetails.includedCharacters.length > 0 && <DetailRow label="Incluido" value={eventDetails.includedCharacters.join(", ")} />}
                      {booking.eventAnimatorAssignments?.length > 0 && <DetailRow label="Animadores" value={booking.eventAnimatorAssignments.map(row=>`${row.resourceName || 'Personaje'}: ${row.employeeName || 'Animador asignado'}`).join(" · ")} />}
                      <DetailRow label="Personajes adicionales" value={characterNames || "Ninguno"} />
                      {eventDetails.selectedExtras.length > 0 && <DetailRow label="Extras" value={eventDetails.selectedExtras.map(extra => `${extra.name} ($${Number(extra.price).toFixed(2)})`).join(", ")} />}
                    </> : null}
                    {locationInfo.zone ? <DetailRow label="Zona" value={`${locationInfo.zone}${locationInfo.fee > 0 ? ` · +$${locationInfo.fee}` : ""}`} /> : null}
                    <DetailRow label="Fecha y hora" value={scheduledLabel} />
                  </div>
                  {canEdit ? <button onClick={() => setRescheduleOpen(true)} type="button" className="evm-inline-action evm-inline-action-v2">Cambiar fecha y hora</button> : null}
                </div>
              </details>

              {(locationInfo.isDelivery || locationInfo.zone || locationInfo.exact) ? <LocationSummaryCard booking={booking} /> : null}

              {canViewPaymentDetails || canViewCommissions ? (
                <details
                  className="evm-card evm-payment-card evm-finance-phase2 evm-finance-disclosure"
                  open={financePanelOpen}
                  onToggle={(event) => setFinancePanelOpen(event.currentTarget.open)}
                >
                  <summary className="evm-finance-summary">
                    <div>
                      <span>Cobro y finanzas</span>
                      <strong>{paymentHeadline}</strong>
                      <small>{compactPaymentLine}</small>
                    </div>
                    <em>{financePanelOpen ? "Ocultar" : canEditPayment ? "Gestionar" : "Ver detalle"}</em>
                  </summary>
                  <div className="evm-finance-body">
                  {booking.paymentReviewRequired === true && (
                    <div role="status" style={{padding: 14, marginBottom: 14, border: "1px solid #b7791f", borderRadius: 10, background: "#fffbeb", color: "#744210"}}>
                      <strong>Pago pendiente de revisión</strong>
                      <p>{booking.paymentReviewReason === "payment-after-terminal-booking"
                        ? "Se recibió un pago después de cerrar la reserva. La reserva conserva su estado."
                        : booking.paymentReviewReason === "payment-after-hold-expired"
                          ? "El pago llegó después de vencer el plazo. Comprueba nuevamente el horario y el equipo antes de confirmar."
                        : booking.paymentReviewReason === "payment-before-business-approval"
                          ? "Se recibió un pago antes de aprobar el evento. Revisa la solicitud antes de confirmar."
                        : booking.paymentReviewReason === "paid-booking-cancelled"
                          ? "Esta reserva se canceló con dinero recibido."
                          : "Este pago necesita una comprobación antes de realizar ajustes."}</p>
                      <p>Comprueba la transacción en Wompi y el importe registrado. Esta alerta no significa que se haya devuelto dinero.</p>
                    </div>
                  )}

                  <div className="evm-payment-grid evm-payment-grid-primary">
                    <label><span>Cobro</span><select disabled={!canEditPayment} value={paymentDraft.paymentStatus} onChange={(e) => setPaymentField("paymentStatus", e.target.value)}><option value="pending">Pendiente</option><option value="paid">Pagado</option><option value="covered">Cubierto</option><option value="partial">Parcial</option><option value="refunded">Reembolsado</option></select></label>
                    <label><span>Pago</span><select disabled={!canEditPayment} value={paymentDraft.paymentMethod} onChange={(e) => setPaymentField("paymentMethod", e.target.value)}><option value="cash">Efectivo</option><option value="yappy">Yappy</option><option value="card">Tarjeta</option><option value="transfer">Transferencia</option><option value="membership">Membresía</option><option value="loyalty_reward">Fidelidad</option><option value="courtesy">Cortesía</option><option value="other">Otro</option></select></label>
                    <label><span>Monto cobrado</span><input disabled={!canEditPayment || String(paymentDraft.paymentStatus || "").toLowerCase() === "covered"} inputMode="decimal" value={String(paymentDraft.paymentStatus || "").toLowerCase() === "covered" ? "" : paymentDraft.amountCharged} onChange={(e) => setPaymentField("amountCharged", e.target.value.replace(/[^0-9.]/g, ""))} placeholder={String(paymentDraft.paymentStatus || "").toLowerCase() === "covered" ? "Cubierto" : String(bookingTotalForPayment || 0)} /></label>
                  </div>

                  <div className="evm-payment-preview evm-payment-preview-hero">
                    <div><span>Cobrado</span><strong>{money(paymentPreview.charged)}</strong></div>
                    {canViewCommissions ? <div><span>Pago trabajador</span><strong>{money(paymentPreview.payout)}</strong></div> : null}
                    {canViewFinancials ? <div><span>Neto negocio</span><strong>{money(paymentPreview.net)}</strong></div> : null}
                    {!canViewCommissions && !canViewFinancials ? <div><span>Detalle</span><strong>Limitado</strong></div> : null}
                  </div>


                  <details className="evm-payment-advanced evm-online-payment-disclosure">
                    <summary>Pago online / Wompi</summary>
                    <div className="evm-wompi-card">
                      <div className="evm-wompi-head"><div><span>Pago online</span><strong>Wompi Checkout</strong><small>{paymentProvider === "wompi" ? "Backbone listo para widget seguro dentro de Loop." : "Preparado para activar Wompi."}</small></div><em>{booking.paymentSessionStatus || booking.payment?.sessionStatus || "no creado"}</em></div>
                      <div className="evm-wompi-grid"><div><span>Total</span><strong>{money(bookingTotalForPayment)}</strong></div><div><span>Abono sugerido</span><strong>{money(depositAmount)}</strong></div><div><span>Saldo</span><strong>{money(balanceAmount)}</strong></div></div>
                      {paymentReference ? <div className="evm-wompi-ref"><span>Referencia</span><strong>{paymentReference}</strong></div> : null}
                      {canCreateWompiPayment ? <div className="evm-wompi-actions"><button type="button" onClick={() => handleCreateWompiSession("deposit")} disabled={wompiSessionLoading}>{wompiSessionLoading ? "Preparando..." : "Preparar abono"}</button><button type="button" onClick={() => handleCreateWompiSession("full")} disabled={wompiSessionLoading}>Preparar pago total</button>{wompiSession?.widget ? <button type="button" className="success" onClick={handleOpenWompiWidget} disabled={wompiSessionLoading}>Abrir widget Wompi</button> : null}{paymentUrl ? <button type="button" onClick={() => window.open(paymentUrl, "_blank", "noopener,noreferrer")}>Checkout fallback</button> : null}<button type="button" onClick={handleMarkMockPaid} disabled={wompiSessionLoading || (!paymentReference && !wompiSession)}>Simular aprobado</button></div> : null}
                      <div className={`evm-wompi-status ${String(wompiSessionStatus || "").toLowerCase().includes("no pudimos") ? "is-error" : ""}`}>{wompiSessionStatus || "F2B sandbox: puedes abrir el widget real de Wompi. No cobra dinero real mientras uses llaves pub_test/prv_test."}</div>
                    </div>
                  </details>

                  <details className="evm-payment-advanced" open={hasPaymentAdvanced}>
                    <summary>Detalles avanzados de descuento, propina y comisión</summary>
                    <div className="evm-payment-grid">
                      <label><span>Descuento</span><input disabled={!canEditPayment} inputMode="decimal" value={paymentDraft.discountAmount} onChange={(e) => setPaymentField("discountAmount", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" /></label>
                      <label><span>Propina</span><input disabled={!canEditPayment} inputMode="decimal" value={paymentDraft.tipAmount} onChange={(e) => setPaymentField("tipAmount", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" /></label>
                      {(canViewCommissions || canEditPayment) ? <label><span>Comisión</span><select disabled={!canEditPayment} value={paymentDraft.workerCommissionType} onChange={(e) => setPaymentField("workerCommissionType", e.target.value)}><option value="none">Sin comisión</option><option value="percentage">Porcentaje</option><option value="fixed">Monto fijo</option></select></label> : null}
                      {(canViewCommissions || canEditPayment) && paymentDraft.workerCommissionType !== "none" ? <label><span>{paymentDraft.workerCommissionType === "percentage" ? "% comisión" : "Monto comisión"}</span><input disabled={!canEditPayment} inputMode="decimal" value={paymentDraft.workerCommissionValue} onChange={(e) => setPaymentField("workerCommissionValue", e.target.value.replace(/[^0-9.]/g, ""))} /></label> : null}
                      {(canViewCommissions || canEditPayment) ? <label><span>Pago trabajador</span><select disabled={!canEditPayment} value={paymentDraft.workerPayoutStatus} onChange={(e) => setPaymentField("workerPayoutStatus", e.target.value)}><option value="pending">Pendiente</option><option value="paid">Pagado</option></select></label> : null}
                      {(canViewCommissions || canEditPayment) ? <label><span>Notas payout</span><input disabled={!canEditPayment} value={paymentDraft.workerPaymentNotes} onChange={(e) => setPaymentField("workerPaymentNotes", e.target.value)} placeholder="Ej. pagar viernes" /></label> : null}
                    </div>
                    <div className="evm-payment-options">
                      <label><input type="checkbox" disabled={!canEditPayment} checked={paymentDraft.commissionOnlyCompleted !== false} onChange={(e) => setPaymentField("commissionOnlyCompleted", e.target.checked)} /> Solo aplicar comisión si la reserva está completada</label>
                    </div>
                  </details>

                  {canEditPayment ? <button type="button" className="evm-payment-save" onClick={() => savePayment()} disabled={paymentSaving}>{paymentSaving ? "Guardando..." : "Guardar cobro"}</button> : null}
                  </div>
                </details>
              ) : null}

              {(canViewInventory || canViewFinancials) ? (
                <section className="evm-card evm-retail-card">
                  <div className="evm-section-title"><span>Venta adicional</span><strong>Productos vendidos</strong><em>{money(productSalesDisplayTotal)}</em></div>
                  <div className="evm-retail-summary-row"><div><span>Venta productos</span><strong>{money(productSalesDisplayTotal)}</strong></div><div><span>Comisión productos</span><strong>{money(productSalesDraftPayout)}</strong></div></div>
                  <div className="evm-retail-deduction-hint"><span>{productSalesDeductionHint}</span>{productSalesStockIssues.length ? <strong>{productSalesStockIssues.length} alerta(s) de stock</strong> : null}</div>
                  <details className="evm-payment-advanced evm-retail-details" open={productSalesDraft.length > 0}>
                    <summary>{productSalesDraft.length ? "Editar productos vendidos" : "Agregar producto vendido"}</summary>
                    <div className="evm-retail-list-edit">
                      {productSalesDraft.map((row, idx) => {
                        const selectedItem = inventoryItems.find((item) => String(item.id) === String(row.itemId));
                        const productName = selectedItem?.name || selectedItem?.title || row.itemName || "Producto vendido";
                        const qty = retailNumber(row.quantity, 0);
                        const unitPrice = retailNumber(row.unitPrice, 0);
                        const lineTotal = Math.max(0, qty * unitPrice);
                        const commissionType = normalizeCommissionType(row.workerCommissionType);
                        const commissionValue = retailNumber(row.workerCommissionValue, 0);
                        const linePayout = commissionType === "percentage"
                          ? Math.max(0, lineTotal * (commissionValue / 100))
                          : commissionType === "fixed"
                            ? Math.max(0, commissionValue)
                            : 0;
                        const lineCost = Math.max(0, qty * retailNumber(row.unitCost || selectedItem?.unitCost || selectedItem?.cost || 0, 0));
                        const lineNet = Math.max(0, lineTotal - linePayout - lineCost);
                        const productStock = getProductStock({ ...selectedItem, availableStockAtSale: row.availableStockAtSale });
                        const hasStockWarning = !allowNegativeProductStock && productStock !== null && qty > productStock;

                        return (
                          <article className="evm-retail-product-card" key={row.id || idx}>
                            <div className="evm-retail-product-head">
                              <div>
                                <span>Producto {idx + 1}</span>
                                <strong>{productName}</strong>
                              </div>
                              {canEditProductSales ? (
                                <button type="button" className="evm-retail-remove" onClick={() => removeProductSaleRow(idx)}>Quitar</button>
                              ) : null}
                            </div>

                            <div className="evm-retail-product-grid">
                              <label className="evm-retail-field evm-retail-field-product">
                                <span>Producto</span>
                                <select disabled={!canEditProductSales} value={row.itemId} onChange={(e) => updateProductSaleRow(idx, "itemId", e.target.value)}>
                                  <option value="">Seleccionar producto</option>
                                  {inventoryItems.map((item) => {
                                    const stock = getProductStock(item);
                                    const label = `${item.name || item.title || item.id}${stock !== null ? ` · stock ${stock}` : ""}`;
                                    return <option key={item.id} value={item.id} disabled={!allowNegativeProductStock && stock !== null && stock <= 0}>{label}</option>;
                                  })}
                                </select>
                              </label>
                              <label className="evm-retail-field"><span>Cantidad</span><input disabled={!canEditProductSales} inputMode="decimal" value={row.quantity} onChange={(e) => updateProductSaleRow(idx, "quantity", e.target.value.replace(/[^0-9.]/g, ""))} /></label>
                              <label className="evm-retail-field"><span>Precio venta</span><input disabled={!canEditProductSales} inputMode="decimal" value={row.unitPrice} onChange={(e) => updateProductSaleRow(idx, "unitPrice", e.target.value.replace(/[^0-9.]/g, ""))} /></label>
                              <label className="evm-retail-field"><span>Comisión</span><select disabled={!canEditProductSales} value={row.workerCommissionType} onChange={(e) => updateProductSaleRow(idx, "workerCommissionType", e.target.value)}><option value="none">Sin comisión</option><option value="percentage">Porcentaje</option><option value="fixed">Monto fijo</option></select></label>
                              {commissionType !== "none" ? <label className="evm-retail-field"><span>{commissionType === "percentage" ? "% comisión" : "Monto comisión"}</span><input disabled={!canEditProductSales} inputMode="decimal" value={row.workerCommissionValue} onChange={(e) => updateProductSaleRow(idx, "workerCommissionValue", e.target.value.replace(/[^0-9.]/g, ""))} /></label> : null}
                            </div>

                            <div className="evm-retail-line-summary">
                              <div><span>Venta</span><strong>{money(lineTotal)}</strong></div>
                              <div><span>Comisión</span><strong>{money(linePayout)}</strong></div>
                              {(canViewInventoryCosts || canViewFinancials) ? <div><span>Neto producto</span><strong>{money(lineNet)}</strong></div> : null}
                              <div><span>Stock</span><strong>{productStock === null ? "Sin registro" : productStock}</strong></div>
                            </div>
                            {hasStockWarning ? <div className="evm-retail-stock-warning">La cantidad supera el stock disponible.</div> : null}
                          </article>
                        );
                      })}
                      {canEditProductSales ? <button type="button" className="evm-retail-add" onClick={addProductSaleRow}>+ Agregar producto</button> : null}
                    </div>
                  </details>
                  {canEditProductSales ? <button type="button" className="evm-payment-save" onClick={saveProductSales} disabled={productSalesSaving || !productSalesCanSave}>{productSalesSaving ? "Guardando..." : productSalesStockIssues.length ? "Corrige stock para guardar" : "Guardar productos vendidos"}</button> : null}
                </section>
              ) : null}

              {canViewInventoryCosts && serviceMaterialRows.length ? (
                <section className="evm-card evm-inventory-card">
                  <div className="evm-section-title"><span>Costos internos</span><strong>Materiales del servicio</strong><em>{serviceMaterialRows.some((row) => row.estimated) ? "Estimado" : inventoryStatusLabel(booking)}</em></div>
                  <div className="evm-inventory-list">
                    {serviceMaterialRows.map((row, idx) => (
                      <div key={`${row.itemId || idx}-${idx}`}>
                        <span>{row.itemName || row.itemId || "Producto"}<em>{row.quantityUsed || row.quantity || 0} {row.unit || "un."} · {money(row.unitCost || 0)} c/u</em></span>
                        <strong>{money(row.totalCost || 0)}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="evm-payment-preview"><div><span>Costo materiales</span><strong>{money(serviceMaterialCost)}</strong></div><div><span>Estado</span><strong>{serviceMaterialRows.some((row) => row.estimated) ? "Se descuenta al completar" : inventoryStatusLabel(booking)}</strong></div></div>
                </section>
              ) : null}
            </main>

            <HistoryRail clientHistory={clientHistory} />
          </div>
        </div>


        {rescheduleOpen ? (
          <div className="evm-reschedule-overlay" role="dialog" aria-modal="true" aria-label="Reprogramar reserva">
            <button type="button" className="evm-reschedule-backdrop" aria-label="Cerrar reprogramación" onClick={() => setRescheduleOpen(false)} />
            <section className="evm-reschedule-panel">
              <div className="evm-reschedule-head">
                <div>
                  <span>Reprogramar</span>
                  <strong>Cambiar fecha y hora</strong>
                  <small>Actualiza la reserva sin borrarla ni perder datos.</small>
                </div>
                <button type="button" onClick={() => setRescheduleOpen(false)} aria-label="Cerrar">×</button>
              </div>

              <div className="evm-reschedule-grid">
                <label><span>Fecha</span><input type="date" value={rescheduleDraft.date} onChange={(e) => updateRescheduleDraft("date", e.target.value)} /></label>
                <label><span>Hora</span><input type="time" step="900" value={rescheduleDraft.time} onChange={(e) => updateRescheduleDraft("time", e.target.value)} /></label>
                <label><span>Duración</span><select value={rescheduleDraft.duration} onChange={(e) => updateRescheduleDraft("duration", e.target.value)}><option value="15">15 min</option><option value="30">30 min</option><option value="45">45 min</option><option value="60">1 hora</option><option value="90">1h 30m</option><option value="120">2 horas</option><option value="180">3 horas</option><option value="240">4 horas</option></select></label>
                <label className="wide"><span>Trabajador</span><select value={rescheduleDraft.employeeId} onChange={(e) => updateRescheduleDraft("employeeId", e.target.value)}><option value="">Sin trabajador / cualquiera</option>{employees.map((emp) => <option key={emp.id} value={emp.id}>{evmWorkerLabel(emp)}</option>)}</select></label>
              </div>


              {rescheduleConflicts.length ? (
                <div className="evm-reschedule-warning">
                  <strong>Hay posible conflicto de horario</strong>
                  {rescheduleConflicts.map((item) => <span key={item.id || `${item.start?.getTime()}-${item.title}`}>{evmTimeInputValue(item.start)} · {item.customerName} · {item.title}</span>)}
                </div>
              ) : (
                <div className="evm-reschedule-ok">No vemos conflictos claros para este horario.</div>
              )}

              <div className="evm-reschedule-actions">
                <button type="button" onClick={() => setRescheduleOpen(false)}>Cancelar</button>
                <button type="button" className="primary" disabled={!canReschedule || rescheduleSaving} onClick={saveReschedule}>{rescheduleSaving ? "Guardando..." : rescheduleConflicts.length ? "Guardar de todas formas" : "Guardar nueva fecha"}</button>
              </div>
            </section>
          </div>
        ) : null}


        {lifecyclePrompt ? (
          <div className="evm-lifecycle-prompt" role="dialog" aria-modal="true" aria-label="Confirmar estado y pago">
            <div className="evm-lifecycle-prompt-card">
              {lifecyclePrompt.type === "complete_without_payment" ? (
                <>
                  <span>Reserva completada sin pago</span>
                  <strong>¿Cómo quieres manejar el cobro?</strong>
                  <p>Puedes registrar el pago ahora o dejarla como completada pendiente de cobro. Así Finanzas verá la diferencia correctamente.</p>
                  <div className="evm-lifecycle-prompt-actions">
                    <button type="button" onClick={() => setLifecyclePrompt(null)}>Cancelar</button>
                    <button type="button" onClick={() => { setLifecyclePrompt(null); onStatusChange("completed"); }}>Completar pendiente de cobro</button>
                    <button type="button" className="primary" onClick={preparePaymentForCompletion}>Registrar pago ahora</button>
                  </div>
                </>
              ) : (
                <>
                  <span>Pago registrado</span>
                  <strong>¿También quieres completar la reserva?</strong>
                  <p>Si el cliente ya fue atendido, guarda el pago y marca la reserva como completada. Si todavía no ocurrió, guarda solo el pago.</p>
                  <div className="evm-lifecycle-prompt-actions">
                    <button type="button" onClick={() => setLifecyclePrompt(null)}>Cancelar</button>
                    <button type="button" onClick={() => { setLifecyclePrompt(null); savePayment({ skipCompletionPrompt: true }); }}>Guardar solo pago</button>
                    <button type="button" className="primary" onClick={() => { setLifecyclePrompt(null); savePayment({ skipCompletionPrompt: true, completeAfter: true }); }}>Guardar pago y completar</button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}

        <footer className="evm-footer">
          {visibleStatusItems.length && onStatusChange ? (
            <div className="evm-status-flow" role="group" aria-label="Cambiar estado de reserva">
              <div className="evm-status-copy"><span>Estado</span><strong>{statusInfo.label}</strong></div>
              <div className="evm-status-actions">
                {visibleStatusItems.map((item) => (
                  <button type="button" key={item.value} className={`is-${item.tone} ${status === item.value ? "is-active" : ""}`} disabled={statusWorkflowSaving} onClick={() => requestStatusChange(item.value)} title={item.helper}>{item.label}</button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="evm-actions evm-actions-v2">
            {showPendingButtons && canAccept && !booking.eventQuote && (!isApprovalFirstEvent(booking) || isOwnerOrAdmin) ? <button onClick={onAccept} disabled={statusWorkflowSaving} className="soft-ok" type="button">{statusWorkflowSaving ? 'Procesando…' : isApprovalFirstEvent(booking) ? 'Aprobar evento' : 'Aceptar reserva'}</button> : null}
            {showPendingButtons && canReject ? <button onClick={onReject} className="soft-danger" type="button">Rechazar</button> : null}
            {canSendManualWhatsApp ? <button onClick={handleSendManualWhatsApp} className="soft-ok" type="button" disabled={whatsappManualSending}>{whatsappManualSending ? "Enviando..." : "Enviar WhatsApp"}</button> : null}

            {(canEdit || canReverseLoyalty || canCancel) ? (
              <details className="evm-more-actions">
                <summary>Más acciones</summary>
                <div className="evm-more-actions-menu">
                  {canEdit ? <button onClick={handleReprocessLoyalty} className="secondary" type="button" disabled={loyaltyReprocessSending}>{loyaltyReprocessSending ? "Reprocesando..." : "Reprocesar fidelidad"}</button> : null}
                  {canReverseLoyalty ? <button onClick={handleReverseLoyaltyRedemption} className="secondary danger" type="button" disabled={loyaltyReverseSending}>{loyaltyReverseSending ? "Deshaciendo..." : "Deshacer fidelidad"}</button> : null}
                  {canEdit && loyaltyRewardApplied && loyaltyWorkerPayoutPaid ? <button className="secondary" type="button" disabled title="La comisión del trabajador ya fue marcada como pagada">Fidelidad no reversible</button> : null}
                  {canEdit && (booking?.membershipApplied === true || booking?.membershipBenefitApplied === true) ? <button onClick={handleReprocessMembershipBenefit} className="secondary" type="button" disabled={membershipBenefitSending}>{membershipBenefitSending ? "Procesando..." : "Procesar membresía"}</button> : null}
                  {canCancel && typeof onCancelBooking === "function" ? <button onClick={onCancelBooking} className="secondary danger" type="button">Cancelar y notificar cliente</button> : null}
                  {canCancel && typeof onRemoveFromAgenda === "function" ? <button onClick={onRemoveFromAgenda} className="secondary" type="button" title="No se notificará al cliente. Solo se libera el espacio visual.">Quitar de agenda</button> : null}
                </div>
              </details>
            ) : null}
          </div>
          {(whatsappManualStatus || loyaltyReprocessStatus || loyaltyReverseStatus || membershipCreditManualOnlyNotice || membershipSummary || membershipBenefitStatus) ? (
            <details className="evm-compact-details evm-footer-details">
              <summary><span>Detalles y últimos procesos</span><em>{membershipSummary ? "Membresía activa" : whatsappReadiness.label}</em></summary>
              <div className="evm-footer-detail-stack">
                <div className={`evm-whatsapp-status ${whatsappReadiness.tone}`}>{whatsappReadiness.label}</div>
                {whatsappManualStatus ? <div className={`evm-whatsapp-status ${whatsappManualStatus.toLowerCase().includes("no ") || whatsappManualStatus.toLowerCase().includes("error") || whatsappManualStatus.toLowerCase().includes("permitió") ? "is-error" : ""}`}>{whatsappManualStatus}</div> : null}
                {loyaltyReprocessStatus ? <div className={`evm-whatsapp-status ${/no pude|no se aplic|error|falta/i.test(loyaltyReprocessStatus) ? "is-error" : ""}`}>{loyaltyReprocessStatus}</div> : null}
                {loyaltyReverseStatus ? <div className={`evm-whatsapp-status ${/no pude|no puedes|error|falta/i.test(loyaltyReverseStatus) ? "is-error" : ""}`}>{loyaltyReverseStatus}</div> : null}
                {membershipCreditManualOnlyNotice ? <div className="evm-membership-policy-note">{membershipCreditManualOnlyNotice}</div> : null}
                {membershipSummary ? (
                  <section className={`evm-membership-summary is-${membershipSummary.state}`} aria-label="Membresía aplicada">
                    <div>
                      <span>Membresía aplicada</span>
                      <strong>{membershipSummary.planName}</strong>
                      <small>{membershipSummary.label}</small>
                    </div>
                    <em>{membershipSummary.stateLabel}</em>
                  </section>
                ) : null}
                {membershipBenefitStatus ? <div className={`evm-whatsapp-status ${/no pude|no se aplic|error|falta|no tiene/i.test(membershipBenefitStatus) ? "is-error" : ""}`}>{membershipBenefitStatus}</div> : null}
              </div>
            </details>
          ) : null}
        </footer>
      </div>
    </AccessibleModal>
  );
}

const eventViewModalStyles = `
.event-view-fit-modal,.event-view-fit-modal *{box-sizing:border-box}.event-view-fit-modal{width:100%;max-height:calc(100dvh - 72px);display:flex;flex-direction:column;overflow:hidden;color:#0f172a;background:#fff;border-radius:26px;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.evm-phase2{box-shadow:0 24px 80px rgba(15,23,42,.14)}.evm-header{flex:0 0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:2px 0 14px;border-bottom:1px solid #eef2f7}.evm-header-main{min-width:0}.evm-kicker{color:#008878;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.14em;margin-bottom:6px}.evm-title-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0}.evm-title-row h2{margin:0;color:#0f172a;font-size:clamp(24px,2.2vw,32px);line-height:1.02;font-weight:950;letter-spacing:-.052em}.evm-header p{margin:6px 0 0;color:#64748b;font-size:13px;font-weight:780;line-height:1.35}.evm-header-actions{display:flex;align-items:center;gap:10px;flex:0 0 auto}.evm-close,.evm-reprogram-btn{border:1px solid #e2e8f0;background:#fff;cursor:pointer;border-radius:999px;padding:10px 16px;font-weight:950;color:#0f172a;box-shadow:0 8px 22px rgba(15,23,42,.06);white-space:nowrap}.evm-reprogram-btn{background:#f0fdfa;border-color:#99f6e4;color:#00675f}.evm-status-pill{border-radius:999px;padding:6px 10px;font-size:11px;font-weight:950;border:1px solid #e2e8f0;background:#f8fafc;color:#475569;line-height:1}.evm-status-pill.is-confirmed{background:#ecfdf5;color:#047857;border-color:#99f6e4}.evm-status-pill.is-pending{background:#fffbeb;color:#b45309;border-color:#fde68a}.evm-status-pill.is-progress{background:#eff6ff;color:#1d4ed8;border-color:#bfdbfe}.evm-status-pill.is-completed{background:#ecfeff;color:#0e7490;border-color:#a5f3fc}.evm-status-pill.is-warning{background:#fff7ed;color:#c2410c;border-color:#fed7aa}.evm-status-pill.is-cancelled{background:#fef2f2;color:#dc2626;border-color:#fecaca}.evm-scroll-area{flex:1 1 auto;min-height:0;overflow:auto;overflow-x:hidden;padding:14px 2px 14px 0;scrollbar-gutter:stable}.evm-layout{display:grid;gap:16px;align-items:start}.evm-layout.has-history{grid-template-columns:minmax(0,1fr) minmax(300px,330px)}.evm-layout.no-history{grid-template-columns:minmax(0,1fr)}.evm-main-col{min-width:0;display:grid;gap:14px}.evm-card,.evm-command-card{border:1px solid #e2e8f0;border-radius:22px;background:#fff;padding:16px;box-shadow:0 8px 24px rgba(15,23,42,.045);min-width:0;max-width:100%;overflow:hidden}.evm-command-card{background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%);display:grid;gap:14px}.evm-command-head{display:flex;align-items:center;gap:12px;min-width:0}.evm-avatar{width:44px;height:44px;border-radius:16px;background:linear-gradient(135deg,#00675f,#14b8a6);color:#fff;display:inline-flex;align-items:center;justify-content:center;font-weight:950;box-shadow:0 10px 24px rgba(0,103,95,.18);flex:0 0 auto}.evm-command-head span,.evm-section-title span,.evm-mini-fact span,.evm-location-top span,.evm-soft-box span,.evm-history-head span,.evm-history-box span,.evm-history-notes span,.evm-status-copy span{display:block;color:#64748b;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.11em}.evm-command-head strong{display:block;color:#0f172a;font-size:18px;font-weight:950;letter-spacing:-.025em}.evm-command-head small,.evm-mini-fact small{display:block;color:#64748b;font-size:12px;font-weight:750;line-height:1.35;margin-top:3px}.evm-command-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}.evm-mini-fact{border:1px solid #eef2f7;background:#f8fafc;border-radius:16px;padding:12px;min-width:0}.evm-mini-fact strong{display:block;margin-top:5px;color:#0f172a;font-size:14px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.evm-mini-fact.is-money{background:#f0fdfa;border-color:#99f6e4}.evm-mini-fact.is-money strong{color:#00675f}.evm-section-title,.evm-payment-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}.evm-section-title strong,.evm-payment-head strong{display:block;margin-top:3px;color:#0f172a;font-size:17px;font-weight:950}.evm-section-title em,.evm-payment-head em{font-style:normal;border-radius:999px;background:#ecfdf5;color:#047857;border:1px solid #99f6e4;padding:6px 9px;font-size:11px;font-weight:950;white-space:nowrap}.evm-snapshot-card{display:grid;gap:10px}.evm-snapshot-list{border:1px solid #eef2f7;border-radius:16px;background:#fbfdff;padding:2px 12px}.evm-inline-action{width:fit-content;min-height:38px;border-radius:999px;border:1px solid #99f6e4;background:#f0fdfa;color:#00675f;padding:0 14px;font-weight:950;cursor:pointer}.evm-detail-row{display:flex;justify-content:space-between;gap:14px;padding:10px 0;border-bottom:1px solid #eef2f7;min-width:0}.evm-detail-row:last-child{border-bottom:0}.evm-detail-row span{color:#64748b;font-size:13px;font-weight:800}.evm-detail-row strong{color:#0f172a;font-size:13px;text-align:right;font-weight:850;min-width:0;word-break:break-word}.evm-detail-row strong.is-strong{color:#00675f;font-weight:950}.evm-location{background:#f8fafc}.evm-location.is-delivery{border-color:#99f6e4;background:#f0fdfa}.evm-location-top{display:flex;justify-content:space-between;gap:12px;align-items:center}.evm-location-top strong{display:block;color:#0f172a;font-size:17px;font-weight:950;margin-top:4px}.evm-location-top em{border-radius:999px;padding:7px 10px;background:#334155;color:#fff;font-size:11px;font-weight:950;font-style:normal;text-transform:uppercase;white-space:nowrap}.evm-location.is-delivery .evm-location-top em{background:#0f766e}.evm-soft-box{margin-top:12px;border-radius:16px;background:#fff;padding:11px 12px;border:1px solid rgba(226,232,240,.85)}.evm-soft-box strong{display:block;color:#0f172a;font-size:13px;font-weight:900;margin-top:4px;line-height:1.4;word-break:break-word}.evm-warning-copy{margin:10px 0 0;color:#b45309;font-size:12px;font-weight:850}.evm-history-card{border:1px solid #e2e8f0;border-radius:22px;background:#fff;padding:15px;box-shadow:0 14px 40px rgba(15,23,42,.07);min-width:0;position:sticky;top:0;max-height:calc(100dvh - 230px);overflow:auto;z-index:1}.evm-history-head strong{display:block;color:#0f172a;font-size:18px;font-weight:950;margin-top:5px;letter-spacing:-.025em}.evm-history-head small{display:block;color:#64748b;font-size:12px;font-weight:750;line-height:1.45;margin-top:3px}.evm-history-stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;margin-top:14px}.evm-history-stats div{border:1px solid #e2e8f0;border-radius:14px;padding:9px 7px;background:#f8fafc;min-width:0}.evm-history-stats span{display:block;color:#64748b;font-size:8.5px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.evm-history-stats strong{display:block;color:#0f172a;font-size:16px;font-weight:950;margin-top:5px}.evm-history-body{display:grid;grid-template-columns:1fr;gap:10px;margin-top:12px}.evm-history-box{border:1px solid #e2e8f0;border-radius:16px;padding:12px;background:#fff}.evm-history-box strong{display:block;color:#00675f;font-size:14px;font-weight:950;margin-top:5px}.evm-history-box small,.evm-history-box span{display:block;color:#64748b;font-size:12px;font-weight:750;line-height:1.4;margin-top:5px}.evm-history-box.is-recent{display:grid;gap:8px}.evm-history-box.is-recent div{padding-bottom:8px;border-bottom:1px solid #eef2f7}.evm-history-box.is-recent div:last-child{border-bottom:0;padding-bottom:0}.evm-history-box.is-recent strong{color:#0f172a;font-size:13px;margin:0}.evm-history-notes{margin-top:12px;border:1px solid #e2e8f0;border-radius:16px;padding:12px;background:#f8fafc}.evm-history-notes em{display:block;margin-top:7px;color:#475569;font-size:12px;line-height:1.4;font-style:normal;font-weight:750}.evm-payment-card{display:grid;gap:12px}.evm-payment-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.evm-payment-grid-primary{grid-template-columns:repeat(3,minmax(0,1fr))}.evm-payment-grid label{display:grid;gap:5px}.evm-payment-grid label span{color:#64748b;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}.evm-payment-grid input,.evm-payment-grid select{min-height:40px;border-radius:13px;border:1px solid #dbe5ee;background:#fff;color:#0f172a;padding:0 10px;font-size:13px;font-weight:850;outline:none;min-width:0}.evm-payment-preview{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:8px}.evm-payment-preview div{border:1px solid #eef2f7;background:#f8fafc;border-radius:14px;padding:10px}.evm-payment-preview span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}.evm-payment-preview strong{display:block;margin-top:3px;color:#00675f;font-size:15px;font-weight:950}.evm-payment-preview-hero div{background:#f0fdfa;border-color:#99f6e4}.evm-payment-options{display:grid;gap:7px;margin:0 12px 12px}.evm-payment-options label{display:flex;gap:8px;align-items:flex-start;color:#475569;font-size:12px;font-weight:850}.evm-payment-options input{margin-top:2px;accent-color:#00675f}.evm-payment-save{min-height:42px;border:0;border-radius:14px;background:#00675f;color:#fff;font-size:13px;font-weight:950;cursor:pointer}.evm-payment-save:disabled{opacity:.6;cursor:not-allowed}.evm-payment-advanced{border:1px solid #eef2f7;background:#fbfdff;border-radius:15px;padding:0;overflow:hidden}.evm-payment-advanced summary{list-style:none;cursor:pointer;padding:12px 13px;color:#334155;font-size:12px;font-weight:950}.evm-payment-advanced summary::-webkit-details-marker{display:none}.evm-payment-advanced .evm-payment-grid{padding:0 12px 12px}.evm-retail-card{background:linear-gradient(180deg,#ffffff,#fbfdff);display:grid;gap:12px}.evm-retail-summary-row{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin:2px 0 0}.evm-retail-summary-row>div{border:1px solid #e2e8f0;background:#f8fafc;border-radius:16px;padding:11px 12px}.evm-retail-summary-row span{display:block;color:#64748b;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}.evm-retail-summary-row strong{display:block;margin-top:4px;color:#00675f;font-size:17px;font-weight:950;letter-spacing:-.035em}.evm-retail-list-edit{display:grid;gap:12px;padding:0 12px 12px;min-width:0;max-width:100%;overflow:hidden}.evm-retail-product-card{border:1px solid #e8eef5;background:linear-gradient(180deg,#ffffff,#fbfdff);border-radius:18px;padding:14px;display:grid;gap:12px;box-shadow:0 10px 24px rgba(15,23,42,.035);min-width:0;max-width:100%;overflow:hidden}.evm-retail-product-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;min-width:0}.evm-retail-product-head>div{min-width:0}.evm-retail-product-head span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.10em}.evm-retail-product-head strong{display:block;margin-top:3px;color:#0f172a;font-size:15px;font-weight:950;letter-spacing:-.03em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.evm-retail-remove{min-height:32px;border:1px solid #fecaca;background:#fff5f5;color:#dc2626;border-radius:999px;padding:0 12px;font-size:12px;font-weight:950;cursor:pointer;white-space:nowrap;flex:0 0 auto}.evm-retail-product-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(82px,.34fr) minmax(108px,.48fr);gap:10px;align-items:end;min-width:0;max-width:100%}.evm-retail-field{display:grid;gap:6px;min-width:0}.evm-retail-field span{font-size:10px;font-weight:950;color:#64748b;text-transform:uppercase;letter-spacing:.08em}.evm-retail-field input,.evm-retail-field select{width:100%;min-width:0;height:40px;border:1px solid #dbe5ee;border-radius:13px;background:#fff;padding:0 11px;font-size:13px;font-weight:850;color:#0f172a;outline:none}.evm-retail-field-product{min-width:0}.evm-retail-line-summary{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;min-width:0}.evm-retail-line-summary>div{border:1px solid #eef2f7;background:#f8fafc;border-radius:14px;padding:9px 10px;min-width:0}.evm-retail-line-summary span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.evm-retail-line-summary strong{display:block;margin-top:3px;color:#00675f;font-size:14px;font-weight:950}.evm-retail-add{min-height:42px;border:1px dashed #99f6e4;background:#f0fdfa;color:#00675f;border-radius:16px;font-weight:950;cursor:pointer}.evm-retail-card>.evm-payment-save{width:100%;margin-top:0}.evm-inventory-card{display:grid;gap:12px}.evm-inventory-list{display:grid;gap:8px}.evm-inventory-list div{display:flex;align-items:center;justify-content:space-between;gap:12px;border:1px solid #eef2f7;background:#f8fafc;border-radius:14px;padding:10px}.evm-inventory-list span{color:#0f172a;font-size:13px;font-weight:900}.evm-inventory-list span em{display:block;margin-top:3px;color:#64748b;font-style:normal;font-size:11px;font-weight:800}.evm-inventory-list strong{color:#00675f;font-size:13px;font-weight:950;white-space:nowrap}.evm-footer{flex:0 0 auto;padding-top:12px;border-top:1px solid #eef2f7;background:#fff;display:grid;gap:10px}.evm-status-flow{display:grid;grid-template-columns:auto minmax(0,1fr);gap:12px;align-items:center;border:1px solid #e2e8f0;border-radius:18px;background:#f8fafc;padding:10px 12px}.evm-status-copy strong{display:block;color:#0f172a;font-size:14px;font-weight:950;margin-top:3px;white-space:nowrap}.evm-status-actions{display:flex;gap:8px;align-items:center;overflow-x:auto;padding-bottom:1px;scrollbar-width:thin}.evm-status-actions button{border:1px solid #dbe5ee;background:#fff;color:#334155;border-radius:999px;padding:8px 12px;font-size:12px;font-weight:950;white-space:nowrap;cursor:pointer;flex:0 0 auto}.evm-status-actions button.is-active{background:#00675f;color:#fff;border-color:#00675f}.evm-status-actions button:disabled{opacity:.58;cursor:not-allowed}.evm-actions{display:flex;gap:10px;flex-wrap:wrap;align-items:center}.evm-actions button{border-radius:14px;padding:11px 16px;font-weight:950;cursor:pointer;border:1px solid #e2e8f0;background:#fff;color:#334155}.evm-actions .primary{border:0;background:#00675f;color:#fff}.evm-actions .soft-ok{border-color:#99f6e4;background:#ecfdf5;color:#047857}.evm-actions .soft-danger{border-color:#fecaca;background:#fef2f2;color:#dc2626}@media(max-width:980px){.event-view-fit-modal{max-height:calc(100dvh - 44px)}.evm-layout.has-history{grid-template-columns:1fr}.evm-history-card{position:relative;top:auto;max-height:none;order:2}.evm-status-flow{grid-template-columns:1fr}.evm-status-actions{padding-bottom:4px}.evm-scroll-area{padding-right:0}.evm-command-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.evm-retail-product-grid{grid-template-columns:1fr 1fr}.evm-retail-field-product{grid-column:1/-1}}@media(max-width:760px){.evm-payment-grid-primary{grid-template-columns:1fr!important}.evm-header{align-items:stretch}.evm-header-actions{flex-direction:column;align-items:flex-end}.evm-command-grid{grid-template-columns:1fr 1fr}}@media(max-width:640px){.evm-header{display:grid;gap:10px}.evm-header-actions{flex-direction:row;justify-content:space-between;align-items:center}.evm-close,.evm-reprogram-btn{padding:9px 12px}.evm-history-stats{grid-template-columns:repeat(2,minmax(0,1fr))}.evm-actions button{width:100%}.evm-card,.evm-command-card{border-radius:18px;padding:14px}.evm-detail-row{display:grid;gap:4px}.evm-detail-row strong{text-align:left}.evm-status-actions button{padding:8px 10px}.evm-payment-grid,.evm-payment-preview,.evm-retail-summary-row,.evm-retail-product-grid,.evm-retail-line-summary,.evm-command-grid{grid-template-columns:1fr!important}.evm-retail-product-head{align-items:center}.evm-retail-remove{min-height:34px}}
.evm-reschedule-overlay{position:absolute;inset:0;z-index:30;display:grid;place-items:center;padding:18px}.evm-reschedule-backdrop{position:absolute;inset:0;border:0;background:rgba(15,23,42,.32);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);cursor:pointer}.evm-reschedule-panel{position:relative;width:min(560px,100%);border:1px solid rgba(226,232,240,.96);border-radius:26px;background:#fff;box-shadow:0 28px 90px rgba(15,23,42,.26);padding:18px;display:grid;gap:14px}.evm-reschedule-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px}.evm-reschedule-head span{display:block;color:#008878;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.13em}.evm-reschedule-head strong{display:block;color:#0f172a;font-size:24px;font-weight:950;letter-spacing:-.045em;margin-top:4px}.evm-reschedule-head small{display:block;color:#64748b;font-size:13px;font-weight:750;margin-top:3px}.evm-reschedule-head button{width:38px;height:38px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;font-size:22px;font-weight:800;color:#334155;cursor:pointer}.evm-reschedule-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}.evm-reschedule-grid label{display:grid;gap:6px}.evm-reschedule-grid label.wide{grid-column:1/-1}.evm-reschedule-grid span{font-size:10px;font-weight:950;color:#64748b;text-transform:uppercase;letter-spacing:.10em}.evm-reschedule-grid input,.evm-reschedule-grid select{min-height:42px;border:1px solid #dbe5ee;border-radius:14px;background:#fff;color:#0f172a;padding:0 12px;font-size:13px;font-weight:850;outline:none;min-width:0}.evm-reschedule-summary{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}.evm-reschedule-summary div{border:1px solid #ccfbf1;background:#f0fdfa;border-radius:16px;padding:11px;min-width:0}.evm-reschedule-summary span{display:block;color:#0f766e;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}.evm-reschedule-summary strong{display:block;margin-top:5px;color:#0f172a;font-size:14px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.evm-reschedule-warning,.evm-reschedule-ok{border-radius:16px;padding:12px;font-size:12px;font-weight:850;line-height:1.4}.evm-reschedule-warning{border:1px solid #fde68a;background:#fffbeb;color:#92400e;display:grid;gap:5px}.evm-reschedule-warning strong{color:#92400e;font-size:13px}.evm-reschedule-warning span{display:block}.evm-reschedule-ok{border:1px solid #99f6e4;background:#ecfdf5;color:#047857}.evm-reschedule-actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap}.evm-reschedule-actions button{min-height:42px;border-radius:14px;border:1px solid #e2e8f0;background:#fff;color:#334155;padding:0 16px;font-weight:950;cursor:pointer}.evm-reschedule-actions button.primary{border-color:#00675f;background:#00675f;color:#fff}.evm-reschedule-actions button:disabled{opacity:.6;cursor:not-allowed}@media(max-width:640px){.evm-reschedule-overlay{padding:10px}.evm-reschedule-panel{border-radius:22px;padding:15px}.evm-reschedule-grid,.evm-reschedule-summary{grid-template-columns:1fr}.evm-reschedule-actions button{width:100%}}


/* LOOP PHASE 2.2A — Customer 360 tags */
.evm-history-tags{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px}
.evm-history-tag{display:inline-flex;align-items:center;border-radius:999px;padding:5px 9px;font-size:10px;font-weight:950;border:1px solid #e2e8f0;background:#f8fafc;color:#475569}
.evm-history-tag.vip{background:#fff7ed;border-color:#fed7aa;color:#c2410c}
.evm-history-tag.loyal{background:#ecfdf5;border-color:#bbf7d0;color:#047857}
.evm-history-tag.new{background:#eff6ff;border-color:#bfdbfe;color:#1d4ed8}
.evm-history-tag.active{background:#ecfdf5;border-color:#bbf7d0;color:#047857}
.evm-history-tag.risk,.evm-history-tag.warning{background:#fffbeb;border-color:#fde68a;color:#b45309}
.evm-history-next-action{margin-top:10px;border:1px solid #dbeafe;background:#eff6ff;border-radius:15px;padding:10px 11px;display:grid;gap:3px}
.evm-history-next-action span{font-size:10px;font-weight:950;letter-spacing:.06em;text-transform:uppercase;color:#2563eb}
.evm-history-next-action strong{font-size:12px;font-weight:850;color:#0f172a;line-height:1.35}

.evm-wompi-card{border:1px solid #dbeafe;background:linear-gradient(135deg,#f8fbff 0%,#fff 58%,#f0fdfa 100%);border-radius:18px;padding:13px;display:grid;gap:10px}.evm-wompi-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px}.evm-wompi-head span,.evm-wompi-ref span{display:block;color:#00675f;font-size:10px;font-weight:950;letter-spacing:.11em;text-transform:uppercase}.evm-wompi-head strong{display:block;color:#0f172a;font-size:15px;font-weight:950;margin-top:4px}.evm-wompi-head small{display:block;color:#64748b;font-size:12px;font-weight:750;margin-top:3px}.evm-wompi-head em{font-style:normal;border-radius:999px;border:1px solid #bfdbfe;background:#eff6ff;color:#1d4ed8;padding:6px 9px;font-size:10px;font-weight:950;white-space:nowrap}.evm-wompi-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}.evm-wompi-grid div{border:1px solid #e2e8f0;background:#fff;border-radius:13px;padding:9px}.evm-wompi-grid span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.07em}.evm-wompi-grid strong{display:block;color:#0f172a;font-size:13px;font-weight:950;margin-top:5px}.evm-wompi-ref{border:1px dashed #99f6e4;background:#f0fdfa;border-radius:13px;padding:9px;min-width:0}.evm-wompi-ref strong{display:block;margin-top:4px;color:#0f172a;font-size:12px;font-weight:850;word-break:break-all}.evm-wompi-actions{display:flex;gap:8px;flex-wrap:wrap}.evm-wompi-actions button{border:1px solid #99f6e4;background:#fff;color:#00675f;border-radius:999px;min-height:36px;padding:0 12px;font-size:12px;font-weight:950;cursor:pointer}.evm-wompi-actions button:first-child{background:#0f172a;color:#fff;border-color:#0f172a}.evm-wompi-actions button.success{background:#00675f;color:#fff;border-color:#00675f}.evm-wompi-status{color:#64748b;font-size:12px;font-weight:750;line-height:1.4}.evm-wompi-status.is-error{color:#b91c1c}@media(max-width:700px){.evm-wompi-grid{grid-template-columns:1fr}.evm-wompi-actions{display:grid}.evm-wompi-actions button{width:100%}}

.evm-actions .secondary.danger{color:#dc2626!important;border-color:#fecaca!important;background:#fffafa!important;}


/* LOOP EVENT VIEW UX V2 A2.2A — professional shell + progressive disclosure */
.evm-header-v2{
  padding:2px 0 16px!important;
  align-items:center!important;
}
.evm-header-v2 .evm-title-row h2{
  font-size:clamp(24px,2vw,30px)!important;
  letter-spacing:-.045em!important;
}
.evm-header-actions-v2{
  align-items:center!important;
}
.evm-edit-primary{
  min-height:40px;
  border:0;
  border-radius:11px;
  background:#00675f;
  color:#fff;
  padding:0 16px;
  font-size:12px;
  font-weight:950;
  cursor:pointer;
  box-shadow:0 8px 22px rgba(0,103,95,.16);
}
.evm-header-v2 .evm-reprogram-btn{
  min-height:40px;
  border-radius:11px!important;
  background:#fff!important;
  box-shadow:none!important;
}
.evm-close-icon{
  width:40px!important;
  height:40px!important;
  padding:0!important;
  display:grid!important;
  place-items:center!important;
  border-radius:11px!important;
  box-shadow:none!important;
  font-size:22px!important;
  line-height:1!important;
}
.evm-quickbar-v2{
  flex:0 0 auto;
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(180px,1fr));
  gap:0;
  margin:0;
  border-bottom:1px solid #e7edf3;
  background:#fff;
}
.evm-quickbar-item{
  min-width:0;
  min-height:64px;
  padding:11px 16px;
  border:0;
  border-right:1px solid #e8eef3;
  background:#fff;
  text-align:left;
}
.evm-quickbar-item:last-child{border-right:0}
.evm-quickbar-item>span,.evm-quickbar-item div>span{
  display:block;
  color:#718096;
  font-size:9.5px;
  font-weight:950;
  letter-spacing:.10em;
  text-transform:uppercase;
}
.evm-quickbar-item>strong,.evm-quickbar-item div>strong{
  display:block;
  margin-top:4px;
  color:#0f172a;
  font-size:13.5px;
  font-weight:950;
  letter-spacing:-.015em;
}
.evm-quickbar-item>small,.evm-quickbar-item div>small{
  display:block;
  margin-top:3px;
  color:#64748b;
  font-size:10.5px;
  font-weight:800;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
button.evm-quickbar-action{
  appearance:none;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  cursor:pointer;
}
button.evm-quickbar-action:hover{background:#f8fbfb}
button.evm-quickbar-action.is-payment-pending,
button.evm-quickbar-action.is-payment-partial{
  background:linear-gradient(90deg,rgba(255,251,235,.76),#fff 62%);
}
.evm-quickbar-action em{
  flex:0 0 auto;
  border:1px solid #b9e8e1;
  border-radius:9px;
  background:#fff;
  color:#00675f;
  padding:7px 10px;
  font-size:10.5px;
  line-height:1;
  font-style:normal;
  font-weight:950;
}
.evm-quickbar-item.is-ok{background:#f7fffd}
.evm-quickbar-item.is-warning{background:#fffaf0}
.evm-quickbar-item.is-membership{background:#f7fffd}
.evm-scroll-area{padding-top:16px!important}
.evm-main-col{gap:12px!important}
.evm-command-card-ux,.evm-command-card{
  box-shadow:none!important;
  border-radius:16px!important;
}
.evm-command-grid{gap:8px!important}
.evm-mini-fact{
  border-radius:12px!important;
  padding:10px 11px!important;
}
.evm-ux-disclosure{
  padding:0!important;
  border-radius:14px!important;
  box-shadow:none!important;
  overflow:hidden!important;
}
.evm-ux-disclosure>summary,.evm-finance-summary{
  list-style:none;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:14px;
  padding:13px 14px;
  background:#fff;
}
.evm-ux-disclosure>summary::-webkit-details-marker,.evm-finance-summary::-webkit-details-marker{display:none}
.evm-ux-disclosure>summary span,.evm-finance-summary span{
  display:block;
  color:#64748b;
  font-size:9.5px;
  font-weight:950;
  letter-spacing:.10em;
  text-transform:uppercase;
}
.evm-ux-disclosure>summary strong,.evm-finance-summary strong{
  display:block;
  margin-top:4px;
  color:#0f172a;
  font-size:14px;
  font-weight:950;
}
.evm-ux-disclosure>summary em,.evm-finance-summary em{
  flex:0 0 auto;
  border:1px solid #dbe5ee;
  border-radius:9px;
  background:#fff;
  color:#00675f;
  padding:7px 10px;
  font-size:10.5px;
  font-style:normal;
  font-weight:950;
}
.evm-ux-disclosure[open]>summary,.evm-finance-disclosure[open]>.evm-finance-summary{
  border-bottom:1px solid #e8eef3;
  background:#fbfdfd;
}
.evm-ux-disclosure-body{padding:12px 14px 14px}
.evm-inline-action-v2{border-radius:9px!important;background:#fff!important}
.evm-finance-disclosure{
  display:block!important;
  padding:0!important;
  border-radius:14px!important;
  box-shadow:none!important;
  overflow:hidden!important;
}
.evm-finance-summary small{
  display:block;
  margin-top:3px;
  color:#64748b;
  font-size:10.5px;
  font-weight:800;
}
.evm-finance-body{
  display:grid;
  gap:12px;
  padding:14px;
}
.evm-payment-grid input,.evm-payment-grid select{
  border-radius:10px!important;
  min-height:38px!important;
}
.evm-payment-preview div{
  border-radius:11px!important;
  background:#fff!important;
}
.evm-payment-preview-hero div{
  background:#f7fffd!important;
}
.evm-online-payment-disclosure{margin-top:0!important}
.evm-online-payment-disclosure .evm-wompi-card{
  margin:0 12px 12px;
}
.evm-payment-save{
  border-radius:10px!important;
  min-height:40px!important;
}
.evm-footer{
  padding-top:10px!important;
  gap:8px!important;
}
.evm-status-flow{
  border-radius:12px!important;
  padding:8px 10px!important;
  background:#fbfcfd!important;
}
.evm-status-actions button{
  border-radius:9px!important;
  padding:7px 10px!important;
}
.evm-actions-v2{
  justify-content:flex-end!important;
  gap:8px!important;
}
.evm-actions-v2>button{
  border-radius:10px!important;
  padding:9px 13px!important;
}
.evm-more-actions{
  position:relative;
}
.evm-more-actions>summary{
  list-style:none;
  cursor:pointer;
  border:1px solid #e2e8f0;
  border-radius:10px;
  background:#fff;
  color:#334155;
  padding:9px 13px;
  font-size:12px;
  font-weight:950;
}
.evm-more-actions>summary::-webkit-details-marker{display:none}
.evm-more-actions-menu{
  position:absolute;
  right:0;
  bottom:calc(100% + 8px);
  width:min(290px,80vw);
  z-index:40;
  display:grid;
  gap:6px;
  padding:8px;
  border:1px solid #dbe5ee;
  border-radius:12px;
  background:#fff;
  box-shadow:0 18px 50px rgba(15,23,42,.15);
}
.evm-more-actions-menu button{
  width:100%;
  min-height:38px;
  border-radius:9px!important;
  text-align:left;
  padding:8px 10px!important;
}
@media(max-width:760px){
  .evm-header-v2{align-items:stretch!important}
  .evm-header-actions-v2{flex-direction:row!important;flex-wrap:wrap!important;align-items:center!important}
  .evm-edit-primary,.evm-header-v2 .evm-reprogram-btn{flex:1 1 auto}
  .evm-quickbar-v2{grid-template-columns:1fr!important}
  .evm-quickbar-item{border-right:0;border-bottom:1px solid #e8eef3}
  .evm-quickbar-item:last-child{border-bottom:0}
}

`;
